
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { 
  BookOpen, 
  Trophy,
  GraduationCap, 
  Sparkles, 
  Box, 
  ScanEye, 
  HelpCircle, 
  Zap, 
  Home,
  Star,
  Ghost,
  Compass,
  Hexagon,
  Layers,
  Search,
  ChevronRight,
  ChevronDown,
  User,
  LayoutGrid,
  ArrowLeft,
  Settings,
  X,
  ShieldCheck,
  Library,
  Crown,
  Copy,
  Brain,
  Calculator,
  MessageCircle,
  CalendarDays,
  Heart,
} from 'lucide-react';

import DetailView from './components/DetailView';
import Auth from './components/Auth';
import { PSYCH_APPS } from './data/psychTestsData';
import TeacherContactModal from './components/TeacherContactModal';
import MinePanel from './components/MinePanel';
import UserSettingsSections from './components/UserSettingsSections';
import AppToolRenderer from './components/AppToolRenderer';
import { activateMembershipCode, API_BASE, chatApi, checkFeatureTrialAccess, clearStoredUser, fetchFeatureRemainingSummary, FeatureRemainingSummary, getStoredUser, hasContentUnlock, isProMember, refreshStoredUserFromServer, saveStoredUser, StoredUser, submitClientLog, TEXT_MODEL, trackFeatureUsage } from './lib/api';

import { stemsData } from './data/stems';
import { stemCombinationsData } from './data/stemCombinations';
import { doorsData } from './data/doors';
import { godsData } from './data/gods';
import { starsData } from './data/stars';
import { trigramsData } from './data/trigrams';
import { CategoryType, SymbolData, UserProfile } from './types';

const CATEGORIES: { id: CategoryType; label: string; icon: React.ElementType; color: string }[] = [
  { id: 'stems', label: '十天干', icon: Zap, color: 'text-indigo-600 bg-indigo-50' },
  { id: 'stem_combinations', label: '天干组合', icon: Layers, color: 'text-pink-600 bg-pink-50' },
  { id: 'doors', label: '八门', icon: Home, color: 'text-emerald-600 bg-emerald-50' },
  { id: 'stars', label: '九星', icon: Star, color: 'text-amber-600 bg-amber-50' },
  { id: 'gods', label: '八神', icon: Ghost, color: 'text-rose-600 bg-rose-50' },
  { id: 'trigrams', label: '八卦', icon: Compass, color: 'text-cyan-600 bg-cyan-50' },
];

const TOOLS = [
  { id: 'trainer', label: '图文转象意', icon: Sparkles, color: 'text-indigo-200', lightColor: 'text-indigo-600 bg-indigo-50' },
  { id: 'decoder', label: '象意解读器', icon: Box, color: 'text-fuchsia-200', lightColor: 'text-fuchsia-600 bg-fuchsia-50' },
  { id: 'findObject', label: '查找象意', icon: ScanEye, color: 'text-blue-200', lightColor: 'text-blue-600 bg-blue-50' },
  { id: 'quiz', label: '习题训练', icon: HelpCircle, color: 'text-rose-200', lightColor: 'text-rose-600 bg-rose-50' },
  { id: 'knowledgePK', label: '知识 PK', icon: Trophy, color: 'text-amber-200', lightColor: 'text-amber-600 bg-amber-50' },
] as const;

const OTHER_APPS = [
  { id: 'dalurenPan', label: '大六壬起课', icon: Hexagon, color: 'text-amber-100', lightColor: 'text-amber-700 bg-amber-50' },
  { id: 'wanNianLi', label: '万年历', icon: CalendarDays, color: 'text-cyan-100', lightColor: 'text-cyan-700 bg-cyan-50' },
  { id: 'sancaiLiuge', label: '三才六格', icon: Calculator, color: 'text-stone-100', lightColor: 'text-slate-600 bg-slate-50' },
  { id: 'kangxiDictionary', label: '康熙字典', icon: BookOpen, color: 'text-amber-100', lightColor: 'text-amber-700 bg-amber-50' },
  { id: 'namingMaster', label: '起名大师', icon: Crown, color: 'text-yellow-100', lightColor: 'text-yellow-700 bg-yellow-50' },
  { id: 'wuxingClothing', label: '五行穿衣', icon: Library, color: 'text-emerald-100', lightColor: 'text-emerald-700 bg-emerald-50' },
  { id: 'zhengyuanReport', label: '灵魂伴侣', icon: Heart, color: 'text-rose-100', lightColor: 'text-rose-700 bg-rose-50' },
  { id: 'consultTeacher', label: '咨询老师', icon: MessageCircle, color: 'text-sky-100', lightColor: 'text-sky-700 bg-sky-50' },
  { id: 'wence', label: '问测', icon: MessageCircle, color: 'text-purple-100', lightColor: 'text-purple-700 bg-purple-50' },
  { id: 'xiaoyaoWence', label: '逍遥问测', icon: MessageCircle, color: 'text-fuchsia-100', lightColor: 'text-fuchsia-700 bg-fuchsia-50' },
] as const;

const getOtherAppGradient = (appId: string): string => {
  switch (appId) {
    case 'dalurenPan':
      return 'bg-gradient-to-br from-[#8b6b3d] to-[#c28c43]';
    case 'wanNianLi':
      return 'bg-gradient-to-br from-cyan-600 to-sky-700';
    case 'sancaiLiuge':
      return 'bg-gradient-to-br from-slate-600 to-slate-800';
    case 'kangxiDictionary':
      return 'bg-gradient-to-br from-amber-600 to-orange-700';
    case 'namingMaster':
      return 'bg-gradient-to-br from-yellow-600 to-amber-800';
    case 'wuxingClothing':
      return 'bg-gradient-to-br from-emerald-700 to-teal-800';
    case 'zhengyuanReport':
      return 'bg-gradient-to-br from-rose-600 to-fuchsia-700';
    case 'consultTeacher':
      return 'bg-gradient-to-br from-sky-600 to-blue-700';
    default:
      return 'bg-gradient-to-br from-slate-700 to-slate-900';
  }
};

const getOtherAppBorder = (appId: string): string => {
  switch (appId) {
    case 'dalurenPan':
      return 'border-amber-300/40';
    case 'wanNianLi':
      return 'border-cyan-300/40';
    case 'sancaiLiuge':
      return 'border-slate-300/40';
    case 'kangxiDictionary':
      return 'border-amber-300/40';
    case 'namingMaster':
      return 'border-yellow-300/40';
    case 'wuxingClothing':
      return 'border-emerald-300/40';
    case 'zhengyuanReport':
      return 'border-rose-300/40';
    case 'consultTeacher':
      return 'border-sky-300/40';
    default:
      return 'border-slate-400/30';
  }
};

const TOOL_HELP: Record<typeof TOOLS[number]['id'], { desc: string; usage: string }> = {
  trainer: {
    desc: '图片文字→奇门符号',
    usage: '先上传图，再补一句场景描述，点击开始推演即可得到两套不同象意路径。',
  },
  decoder: {
    desc: '宫位符号→物品象意',
    usage: '输入神星门和天盘地盘后点"解码物品"，优先看每条"判定依据"。',
  },
  findObject: {
    desc: '图片+符号→匹配实物',
    usage: '先上传环境图，再填盘面关键符号，点击开始定位查看2个最佳候选。',
  },
  quiz: {
    desc: '象意知识练习',
    usage: '先选板块和题量，做完自动出分并给出薄弱点分析与复盘建议。',
  },
  knowledgePK: {
    desc: '知识PK答题应用',
    usage: '点击开始答题进入比赛流程，逐题作答并查看最终结算分数。',
  },
};

type ToolType = typeof TOOLS[number]['id'] | typeof OTHER_APPS[number]['id'] | (typeof PSYCH_APPS)[number]['id'] | 'theory' | 'xuanxueStories' | 'originMemory' | 'pan' | 'dalurenPan' | 'wence';
type ViewMode = 'dashboard' | 'sidebar_view';
type MobileTab = 'study' | 'practice' | 'mine';
type MobileDockStyle = 'default' | 'ios';
type MineSubPage = 'home' | 'notice' | 'account' | 'display' | 'qimen' | 'archive';
type ArchiveRecordType = 'qimen' | 'daluren' | 'sancai';
type ArchiveToolId = 'pan' | 'dalurenPan' | 'sancaiLiuge';
interface ArchiveToolState {
  tool: ArchiveToolId;
  pendingRecordId: string | null;
}
type LaunchTarget = 'appPage' | 'pan' | 'dalurenPan' | 'sancaiLiuge';
type PanDockTarget = 'pan' | 'dalurenPan' | 'wanNianLi' | 'wuxingClothing' | 'kangxiDictionary' | 'sancaiLiuge';
type StudyCardId = 'theory' | 'originMemory' | 'xuanxueStories';

const QIMEN_ONLY_HOST = 'qimen.aifashou.cn';
const QIMEN_PRACTICE_TOOL_IDS = ['trainer', 'decoder', 'findObject', 'quiz', 'knowledgePK'] as const;
const QIMEN_OTHER_APP_IDS = ['dalurenPan', 'wanNianLi', 'sancaiLiuge', 'kangxiDictionary', 'namingMaster', 'wuxingClothing', 'zhengyuanReport', 'consultTeacher', 'wence', 'xiaoyaoWence'] as const;
const QIMEN_STUDY_CARD_IDS = ['theory', 'originMemory', 'xuanxueStories'] as const;
const QIMEN_ALLOWED_TOOL_IDS = ['pan', ...QIMEN_PRACTICE_TOOL_IDS, ...QIMEN_OTHER_APP_IDS, ...QIMEN_STUDY_CARD_IDS, 'psychSbti', 'psychMbti', 'psychMbtiStage', 'psychBeauty', 'psychScl90', 'psychBipolar', 'psychSexRepress', 'psychQuirk', 'psychPreference', 'psychRole', 'psychAbo', 'psychDark', 'psychTalent', 'psychNeeds', 'psychCity', 'psychEnneagram'] as const;
const QIMEN_ALLOWED_DEFAULT_LAUNCH_TARGETS = ['appPage', 'pan'] as const;
const QIMEN_ALLOWED_PAN_DOCK_TARGETS = ['pan'] as const;
const QIMEN_PRACTICE_TOOL_ID_SET = new Set<string>(QIMEN_PRACTICE_TOOL_IDS);
const QIMEN_OTHER_APP_ID_SET = new Set<string>(QIMEN_OTHER_APP_IDS);
const QIMEN_STUDY_CARD_ID_SET = new Set<string>(QIMEN_STUDY_CARD_IDS);
const QIMEN_ALLOWED_TOOL_ID_SET = new Set<string>(QIMEN_ALLOWED_TOOL_IDS);
const QIMEN_ALLOWED_DEFAULT_LAUNCH_TARGET_SET = new Set<string>(QIMEN_ALLOWED_DEFAULT_LAUNCH_TARGETS);
const QIMEN_ALLOWED_PAN_DOCK_TARGET_SET = new Set<string>(QIMEN_ALLOWED_PAN_DOCK_TARGETS);
interface AppLayoutCategoryConfig {
  id: string;
  label: string;
  order: number;
  appIds: string[];
}
interface AppLayoutProfileConfig {
  categories: AppLayoutCategoryConfig[];
}
interface AppLayoutProfilesConfig {
  desktop: AppLayoutProfileConfig;
  mobile: AppLayoutProfileConfig;
}
const WECHAT_ID = 'dao6257';
const PRO_LEARN_CATEGORY_IDS: CategoryType[] = ['doors', 'stars', 'gods', 'trigrams'];
const WENCE_PRO_UNLOCK_ID = 'pro:wence';
const WENCE_PRO_BUILTIN_USERS = new Set(['930198090@qq.com', 'jiajiaolivialv@163.com']);
const PRO_OTHER_APP_IDS: (typeof OTHER_APPS[number]['id'])[] = ['wence', 'xiaoyaoWence'];
const SVIP_OTHER_APP_IDS = new Set<typeof OTHER_APPS[number]['id']>(['namingMaster', 'zhengyuanReport', 'sancaiLiuge']);
const LOCKED_OTHER_APP_IDS = new Set<typeof OTHER_APPS[number]['id']>([
  ...PRO_OTHER_APP_IDS,
  ...Array.from(SVIP_OTHER_APP_IDS),
]);
const APP_NAME = '轩辕国学';
const PAN_SETTINGS_KEY = 'qimen_pan_settings_v2';
const DEFAULT_LAUNCH_TARGET_KEY = 'qimen_default_launch_target_v1';
const PAN_DOCK_TARGET_KEY = 'qimen_pan_dock_target_v1';
const PAN_SESSION_KEY = 'qimen_pan_session_v1';
const PAN_SESSION_RESTORE_ONCE_KEY = 'qimen_pan_restore_once_v1';
const SIDEBAR_COLLAPSE_KEY = 'qimen_sidebar_collapsed_v1';
const SIDEBAR_SECTIONS_KEY = 'qimen_sidebar_sections_v1';
const MOBILE_APP_COLS_KEY = 'qimen_mobile_app_cols_v1';
const MOBILE_DOCK_STYLE_KEY = 'qimen_mobile_dock_style_v1';
const FOUR_XIANGYI_UNLOCK_CODE = '轩哥牛逼';
const FOUR_XIANGYI_UNLOCK_KEY_PREFIX = 'qimen_four_xiangyi_unlock_v1';
const XIAOYAO_WENCE_UNLOCK_KEY_PREFIX = 'qimen_xiaoyao_wence_unlock_v1';
const NAMING_MASTER_UNLOCK_CODE = '起名大师';
const NAMING_MASTER_UNLOCK_KEY_PREFIX = 'qimen_naming_master_unlock_v1';
const TOOL_TRIAL_USAGE_PREFIX = 'qimen_tool_trial_usage_v1';
const TOOL_TRIAL_LIMIT = 3;
const LIMITED_TRIAL_TOOL_IDS = new Set<ToolType>(['kangxiDictionary']);
const STRICT_MEMBER_TRIAL_TOOL_IDS = new Set<typeof OTHER_APPS[number]['id']>(['kangxiDictionary', 'wuxingClothing', 'wanNianLi']);
const PRO_BADGE_OTHER_APP_IDS = new Set<typeof OTHER_APPS[number]['id']>(['sancaiLiuge', 'kangxiDictionary', 'wuxingClothing', 'wanNianLi']);
const SVIP_TOOL_LABELS = new Set<string>(['起名大师', '灵魂伴侣', '三才六格']);
const getOtherAppCornerLabel = (appId: typeof OTHER_APPS[number]['id']): 'PRO' | 'SVIP' | null => {
  if (SVIP_OTHER_APP_IDS.has(appId)) return 'SVIP';
  if (PRO_OTHER_APP_IDS.includes(appId) || PRO_BADGE_OTHER_APP_IDS.has(appId)) return 'PRO';
  return null;
};
const STUDY_CARD_IDS: StudyCardId[] = ['theory', 'originMemory', 'xuanxueStories'];
const DEFAULT_DESKTOP_APP_LAYOUT_CATEGORIES: AppLayoutCategoryConfig[] = [
  { id: 'other', label: '专业排盘', order: 0, appIds: ['pan', 'dalurenPan', 'wanNianLi', 'sancaiLiuge', 'kangxiDictionary', 'namingMaster', 'wuxingClothing', 'zhengyuanReport', 'consultTeacher', 'wence', 'xiaoyaoWence'] },
  { id: 'practice', label: '奇门学习', order: 1, appIds: ['trainer', 'decoder', 'findObject', 'quiz'] },
  { id: 'study', label: '学习中心', order: 2, appIds: ['theory', 'originMemory', 'xuanxueStories'] },
  { id: 'psych', label: '心理测试', order: 3, appIds: PSYCH_APPS.map((app) => app.id) },
];
const DEFAULT_MOBILE_APP_LAYOUT_CATEGORIES: AppLayoutCategoryConfig[] = [
  { id: 'other', label: '专业排盘', order: 0, appIds: ['pan', 'dalurenPan', 'wanNianLi', 'sancaiLiuge', 'kangxiDictionary', 'namingMaster', 'wuxingClothing', 'zhengyuanReport', 'consultTeacher', 'wence', 'xiaoyaoWence'] },
  { id: 'practice', label: '奇门学习', order: 1, appIds: ['trainer', 'decoder', 'findObject', 'quiz'] },
  { id: 'study', label: '学习中心', order: 2, appIds: ['theory', 'originMemory', 'xuanxueStories'] },
  { id: 'psych', label: '心理测试', order: 3, appIds: PSYCH_APPS.map((app) => app.id) },
];
const LOCKED_HOME_LAYOUT_ORDER = ['other', 'practice', 'study', 'psych'] as const;
const LOCKED_HOME_LAYOUT_LABELS: Record<(typeof LOCKED_HOME_LAYOUT_ORDER)[number], string> = {
  other: '专业排盘',
  psych: '心理测试',
  practice: '奇门学习',
  study: '学习中心',
};
const cloneAppLayoutCategories = (categories: AppLayoutCategoryConfig[]): AppLayoutCategoryConfig[] => (
  categories.map((cat) => ({ ...cat, appIds: [...cat.appIds] }))
);
const createDefaultAppLayoutProfiles = (): AppLayoutProfilesConfig => ({
  desktop: { categories: cloneAppLayoutCategories(DEFAULT_DESKTOP_APP_LAYOUT_CATEGORIES) },
  mobile: { categories: cloneAppLayoutCategories(DEFAULT_MOBILE_APP_LAYOUT_CATEGORIES) },
});
const TOOL_ID_SET = new Set<string>(TOOLS.map((tool) => tool.id));
const OTHER_APP_ID_SET = new Set<string>(OTHER_APPS.map((app) => app.id));
const PSYCH_APP_ID_SET = new Set<string>(PSYCH_APPS.map((app) => app.id));
const NON_AI_USAGE_TOOL_IDS = new Set<string>([
  'pan',
  'quiz',
  'theory',
  'originMemory',
  'xuanxueStories',
  ...OTHER_APPS.map((app) => app.id).filter((id) => id !== 'consultTeacher'),
  ...PSYCH_APPS.map((app) => app.id),
]);
const TOOL_LABEL_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = {
    pan: '奇门排盘',
    theory: '基础知识',
    originMemory: '象意速记',
    xuanxueStories: '玄学故事',
  };
  TOOLS.forEach((item) => { map[item.id] = item.label; });
  OTHER_APPS.forEach((item) => { map[item.id] = item.label; });
  PSYCH_APPS.forEach((item) => { map[item.id] = item.label; });
  return map;
})();
const buildClientSessionId = (): string => (
  `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
);
const toUsageFeatureId = (toolId: string): string => `tool:${String(toolId || '').trim()}`;
const getCurrentPagePath = (): string => {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname || '/'}${window.location.search || ''}`.slice(0, 240);
};
const CLIENT_LOG_LOCAL_STORAGE_KEYS = [
  'qimen_wence_history_v1',
  'qimen_xiaoyao_wence_history_v1',
  'qimen_pan_records_v2',
  'daluren_pan_records_v1',
  'qimen_sancai_liuge_records_v1',
  'qimen_api_base_last_v1',
] as const;
const CLIENT_LOG_SESSION_STORAGE_KEYS = [
  'qimen_wence_state_v1',
  'qimen_xiaoyao_wence_state_v1',
  PAN_SESSION_KEY,
] as const;
const CLIENT_LOG_MAX_DEPTH = 5;
const CLIENT_LOG_MAX_ARRAY_ITEMS = 60;
const CLIENT_LOG_MAX_OBJECT_KEYS = 80;
const CLIENT_LOG_MAX_STRING_CHARS = 3000;

const sanitizeClientLogValue = (value: unknown, depth = 0): unknown => {
  if (value == null) return null;
  if (depth >= CLIENT_LOG_MAX_DEPTH) return '[truncated:depth]';
  if (typeof value === 'string') {
    if (value.length <= CLIENT_LOG_MAX_STRING_CHARS) return value;
    return `${value.slice(0, CLIENT_LOG_MAX_STRING_CHARS)}...[truncated:${value.length}]`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value
      .slice(0, CLIENT_LOG_MAX_ARRAY_ITEMS)
      .map((item) => sanitizeClientLogValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const out: Record<string, unknown> = {};
    entries.slice(0, CLIENT_LOG_MAX_OBJECT_KEYS).forEach(([rawKey, rawValue]) => {
      const key = String(rawKey || '').slice(0, 120);
      if (!key) return;
      out[key] = sanitizeClientLogValue(rawValue, depth + 1);
    });
    if (entries.length > CLIENT_LOG_MAX_OBJECT_KEYS) {
      out.__truncated__ = `keys>${CLIENT_LOG_MAX_OBJECT_KEYS}`;
    }
    return out;
  }
  return String(value);
};

const readStorageSnapshot = (storage: Storage | null, key: string): Record<string, unknown> | null => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (raw == null) return null;
    const entry: Record<string, unknown> = {
      key,
      chars: raw.length,
    };
    try {
      const parsed = JSON.parse(raw);
      entry.value = sanitizeClientLogValue(parsed);
    } catch (err) {
      entry.parseError = err instanceof Error ? err.message : 'parse_error';
      entry.rawPreview = raw.slice(0, 4000);
    }
    return entry;
  } catch (err) {
    return {
      key,
      error: err instanceof Error ? err.message : 'storage_error',
    };
  }
};

const readStorageArrayCount = (storage: Storage | null, key: string): number | null => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return null;
  }
};

type ClientLogSubmitResult = { ok: boolean; msg: string; logId?: string };

type DefaultPanMode = 'simple' | 'pro';

const DEFAULT_LAUNCH_TARGET_OPTIONS: Array<{ id: LaunchTarget; label: string }> = [
  { id: 'pan', label: '奇门排盘' },
  { id: 'dalurenPan', label: '大六壬起课' },
  { id: 'sancaiLiuge', label: '三才六格' },
  { id: 'appPage', label: '应用页面' },
];
const PAN_DOCK_TARGET_OPTIONS: Array<{ id: PanDockTarget; label: string }> = [
  { id: 'pan', label: '奇门排盘' },
  { id: 'dalurenPan', label: '大六壬排盘' },
  { id: 'wanNianLi', label: '万年历' },
  { id: 'wuxingClothing', label: '五行穿衣' },
  { id: 'kangxiDictionary', label: '康熙字典' },
  { id: 'sancaiLiuge', label: '三才六格' },
];
const STUDY_TOOL_PATHS: Record<StudyCardId, string> = {
  theory: '/theory',
  originMemory: '/origin-memory',
  xuanxueStories: '/stories',
};

const resolveLaunchToolFromTarget = (
  target: LaunchTarget,
): 'pan' | 'dalurenPan' | 'sancaiLiuge' | null => {
  if (target === 'pan' || target === 'dalurenPan' || target === 'sancaiLiuge') return target;
  return null;
};

const normalizeDefaultLaunchTarget = (raw: unknown): LaunchTarget => {
  const value = String(raw || '').trim();
  if (value === 'practice' || value === 'other') return 'appPage';
  if (value === 'appPage' || value === 'pan' || value === 'dalurenPan' || value === 'sancaiLiuge') return value as LaunchTarget;
  if (DEFAULT_LAUNCH_TARGET_OPTIONS.some((item) => item.id === value)) return value as LaunchTarget;
  return 'appPage';
};

const normalizeAppPathname = (rawPath: string | null | undefined): string => {
  const plain = String(rawPath || '').trim().split('?')[0].split('#')[0];
  if (!plain) return '/';
  const withLeading = plain.startsWith('/') ? plain : `/${plain}`;
  const compact = withLeading.replace(/\/{2,}/g, '/');
  if (compact === '/') return '/';
  return compact.replace(/\/+$/, '') || '/';
};

const resolveStudyToolFromPath = (pathname: string): StudyCardId | null => {
  const normalized = normalizeAppPathname(pathname);
  const entry = Object.entries(STUDY_TOOL_PATHS).find(([, path]) => path === normalized);
  return (entry?.[0] as StudyCardId | undefined) || null;
};

const resolveRoutePathForTool = (toolId: ToolType | null): string => {
  if (!toolId) return '/';
  if (toolId === 'theory' || toolId === 'originMemory' || toolId === 'xuanxueStories') {
    return STUDY_TOOL_PATHS[toolId];
  }
  return '/';
};

const getRuntimeHost = (): string => {
  if (typeof window === 'undefined') return '';
  return String(window.location.hostname || '').trim().toLowerCase();
};

const isQimenOnlyHost = (host: string): boolean => host === QIMEN_ONLY_HOST;

const isToolAllowedForHost = (toolId: string, host: string): boolean => {
  if (!isQimenOnlyHost(host)) return true;
  return QIMEN_ALLOWED_TOOL_ID_SET.has(toolId);
};

const normalizeLaunchTargetForHost = (target: LaunchTarget, host: string): LaunchTarget => {
  if (!isQimenOnlyHost(host)) return target;
  return QIMEN_ALLOWED_DEFAULT_LAUNCH_TARGET_SET.has(target) ? target : 'appPage';
};

const normalizePanDockTargetForHost = (target: PanDockTarget, host: string): PanDockTarget => {
  if (!isQimenOnlyHost(host)) return target;
  return QIMEN_ALLOWED_PAN_DOCK_TARGET_SET.has(target) ? target : 'pan';
};

const isIosStandaloneMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  const ua = String(nav.userAgent || '');
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  if (!isIos) return false;
  const displayStandalone = typeof window.matchMedia === 'function'
    ? window.matchMedia('(display-mode: standalone)').matches
    : false;
  return nav.standalone === true || displayStandalone;
};

const ProCorner: React.FC<{ onClick?: (e: React.MouseEvent) => void; label?: string }> = ({ onClick, label = 'PRO' }) => (
  <div
    onClick={onClick}
    className="absolute top-1 right-1 lg:top-2 lg:right-2 rounded px-1 py-0.5 text-[8px] lg:rounded-md lg:px-2 lg:py-0.5 lg:text-[10px] font-black tracking-wide text-slate-900 shadow ring-1 ring-amber-100/80 bg-gradient-to-r from-yellow-200 via-amber-300 to-yellow-100"
  >
    {label}
  </div>
);

const getInitialMobileTab = (): MobileTab => {
  const q = new URLSearchParams(window.location.search);
  const tab = q.get('tab');
  if (tab === 'study' || tab === 'mine' || tab === 'practice') return tab;
  return 'practice';
};

const detectDesktopViewport = (): boolean => {
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const ua = String(window.navigator?.userAgent || '');
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(ua);
  if (!mobileUa && width >= 820) return true;
  return width >= 1024;
};

const detectPadViewport = (): boolean => {
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const ua = String(window.navigator?.userAgent || '');
  const iPadUa = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && 'ontouchend' in document);
  if (detectDesktopViewport()) return false;
  if (iPadUa) return true;
  return width >= 768;
};

/** 手机端应用卡片列数：/home-4 为 4 列，其余为 3 列 */
const loadMobileAppCols = (): 3 | 4 => {
  try {
    const raw = localStorage.getItem(MOBILE_APP_COLS_KEY);
    if (raw) return parseInt(raw) as 3 | 4;
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
    return pathname.includes('home-4') ? 4 : 3;
  } catch {
    return 3;
  }
};

const saveMobileAppCols = (cols: 3 | 4): void => {
  localStorage.setItem(MOBILE_APP_COLS_KEY, cols.toString());
};

const normalizeMobileDockStyle = (raw: unknown): MobileDockStyle => {
  const value = String(raw || '').trim().toLowerCase();
  return value === 'default' ? 'default' : 'ios';
};

const loadMobileDockStyle = (): MobileDockStyle => {
  try {
    return normalizeMobileDockStyle(localStorage.getItem(MOBILE_DOCK_STYLE_KEY));
  } catch {
    return 'ios';
  }
};

const saveMobileDockStyle = (style: MobileDockStyle): void => {
  try {
    localStorage.setItem(MOBILE_DOCK_STYLE_KEY, normalizeMobileDockStyle(style));
  } catch {
    // noop
  }
};

const getFourXiangyiUnlockStorageKey = (userId?: string | null): string => {
  const uid = String(userId || '').trim().toLowerCase();
  return `${FOUR_XIANGYI_UNLOCK_KEY_PREFIX}:${uid || 'guest'}`;
};

const loadFourXiangyiUnlocked = (userId?: string | null): boolean => {
  try {
    const key = getFourXiangyiUnlockStorageKey(userId);
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const saveFourXiangyiUnlocked = (userId: string | null | undefined, unlocked: boolean): void => {
  try {
    const key = getFourXiangyiUnlockStorageKey(userId);
    if (unlocked) {
      localStorage.setItem(key, '1');
      return;
    }
    localStorage.removeItem(key);
  } catch {
    // noop
  }
};

const getXiaoyaoWenceUnlockStorageKey = (userId?: string | null): string => {
  const uid = String(userId || '').trim().toLowerCase();
  return `${XIAOYAO_WENCE_UNLOCK_KEY_PREFIX}:${uid || 'guest'}`;
};

const loadXiaoyaoWenceUnlocked = (userId?: string | null): boolean => {
  try {
    const key = getXiaoyaoWenceUnlockStorageKey(userId);
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const saveXiaoyaoWenceUnlocked = (userId: string | null | undefined, unlocked: boolean): void => {
  try {
    const key = getXiaoyaoWenceUnlockStorageKey(userId);
    if (unlocked) {
      localStorage.setItem(key, '1');
      return;
    }
    localStorage.removeItem(key);
  } catch {
    // noop
  }
};

const getNamingMasterUnlockStorageKey = (userId?: string | null): string => {
  const uid = String(userId || '').trim().toLowerCase();
  return `${NAMING_MASTER_UNLOCK_KEY_PREFIX}:${uid || 'guest'}`;
};

const loadNamingMasterUnlocked = (userId?: string | null): boolean => {
  try {
    const key = getNamingMasterUnlockStorageKey(userId);
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const saveNamingMasterUnlocked = (userId: string | null | undefined, unlocked: boolean): void => {
  try {
    const key = getNamingMasterUnlockStorageKey(userId);
    if (unlocked) {
      localStorage.setItem(key, '1');
      return;
    }
    localStorage.removeItem(key);
  } catch {
    // noop
  }
};

const getTrialUsageStorageKey = (toolId: ToolType, userId?: string | null): string => {
  const uid = String(userId || '').trim().toLowerCase() || 'guest';
  return `${TOOL_TRIAL_USAGE_PREFIX}:${uid}:${toolId}`;
};

const loadToolTrialUsage = (toolId: ToolType, userId?: string | null): number => {
  try {
    const raw = localStorage.getItem(getTrialUsageStorageKey(toolId, userId));
    const used = Number.parseInt(String(raw || '0'), 10);
    if (!Number.isFinite(used) || used < 0) return 0;
    return used;
  } catch {
    return 0;
  }
};

const saveToolTrialUsage = (toolId: ToolType, used: number, userId?: string | null): void => {
  try {
    localStorage.setItem(getTrialUsageStorageKey(toolId, userId), String(Math.max(0, Math.trunc(used))));
  } catch {
    // noop
  }
};

type XiangyiCategoryMode = 'full' | 'simple';

interface PanSettingsFull {
  defaultMode?: DefaultPanMode;
  /** 月干为主人公开关。时盘：开启=月干主人公；刻盘：开启=日干作月干为主人公。 */
  remoteMonthAsSubject?: boolean;
  /** 象意分类：full=保留二级分类，simple=简化特定子分类 */
  xiangyiCategoryMode?: XiangyiCategoryMode;
}

const loadPanFullSettings = (): PanSettingsFull => {
  try {
    const raw = localStorage.getItem(PAN_SETTINGS_KEY);
    if (!raw) return { defaultMode: 'simple', remoteMonthAsSubject: false, xiangyiCategoryMode: 'full' };
    const parsed = JSON.parse(raw) as PanSettingsFull;
    return {
      defaultMode: parsed.defaultMode === 'pro' ? 'pro' : 'simple',
      remoteMonthAsSubject: parsed.remoteMonthAsSubject === true,
      xiangyiCategoryMode: parsed.xiangyiCategoryMode === 'simple' ? 'simple' : 'full',
    };
  } catch {
    return { defaultMode: 'simple', remoteMonthAsSubject: false, xiangyiCategoryMode: 'full' };
  }
};

const loadDefaultPanMode = (): DefaultPanMode => {
  return loadPanFullSettings().defaultMode ?? 'simple';
};

const saveDefaultPanMode = (mode: DefaultPanMode): void => {
  const curr = loadPanFullSettings();
  localStorage.setItem(PAN_SETTINGS_KEY, JSON.stringify({ ...curr, defaultMode: mode }));
};

const loadDefaultLaunchTarget = (): LaunchTarget => {
  try {
    return normalizeDefaultLaunchTarget(localStorage.getItem(DEFAULT_LAUNCH_TARGET_KEY));
  } catch {
    return 'appPage';
  }
};

const saveDefaultLaunchTarget = (target: LaunchTarget): void => {
  try {
    localStorage.setItem(DEFAULT_LAUNCH_TARGET_KEY, normalizeDefaultLaunchTarget(target));
  } catch {
    // noop
  }
};

const normalizePanDockTarget = (raw: unknown): PanDockTarget => {
  const value = String(raw || '').trim();
  if (PAN_DOCK_TARGET_OPTIONS.some((item) => item.id === value)) return value as PanDockTarget;
  return 'pan';
};

const loadPanDockTarget = (): PanDockTarget => {
  try {
    return normalizePanDockTarget(localStorage.getItem(PAN_DOCK_TARGET_KEY));
  } catch {
    return 'pan';
  }
};

const savePanDockTarget = (target: PanDockTarget): void => {
  try {
    localStorage.setItem(PAN_DOCK_TARGET_KEY, normalizePanDockTarget(target));
  } catch {
    // noop
  }
};

const loadRemoteMonthAsSubject = (): boolean => {
  return loadPanFullSettings().remoteMonthAsSubject === true;
};

const saveRemoteMonthAsSubject = (value: boolean): void => {
  const curr = loadPanFullSettings();
  localStorage.setItem(PAN_SETTINGS_KEY, JSON.stringify({ ...curr, remoteMonthAsSubject: value }));
};

const loadXiangyiCategoryMode = (): XiangyiCategoryMode => loadPanFullSettings().xiangyiCategoryMode ?? 'full';

const saveXiangyiCategoryMode = (value: XiangyiCategoryMode): void => {
  const curr = loadPanFullSettings();
  localStorage.setItem(PAN_SETTINGS_KEY, JSON.stringify({ ...curr, xiangyiCategoryMode: value }));
};

const loadSidebarCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

const saveSidebarCollapsed = (collapsed: boolean): void => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? '1' : '0');
  } catch {
    // noop
  }
};

type SidebarSectionId = 'practice' | 'other' | 'psych' | 'learn';

const loadSidebarSectionsExpanded = (): Record<SidebarSectionId, boolean> => {
  try {
    const raw = localStorage.getItem(SIDEBAR_SECTIONS_KEY);
    if (!raw) return { practice: true, other: true, psych: true, learn: true };
    const parsed = JSON.parse(raw) as Partial<Record<SidebarSectionId, boolean>>;
    return {
      practice: parsed.practice !== false,
      other: parsed.other !== false,
      psych: parsed.psych !== false,
      learn: parsed.learn !== false,
    };
  } catch {
    return { practice: true, other: true, psych: true, learn: true };
  }
};

const saveSidebarSectionsExpanded = (sections: Record<SidebarSectionId, boolean>): void => {
  try {
    localStorage.setItem(SIDEBAR_SECTIONS_KEY, JSON.stringify(sections));
  } catch {
    // noop
  }
};

const normalizeAppLayoutCategories = (
  categories: unknown,
  fallbackCategories: AppLayoutCategoryConfig[],
): AppLayoutCategoryConfig[] => {
  const fallback = cloneAppLayoutCategories(fallbackCategories);
  if (!Array.isArray(categories) || categories.length === 0) return fallback;
  const normalized: AppLayoutCategoryConfig[] = [];
  categories.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const row = item as { id?: unknown; label?: unknown; order?: unknown; appIds?: unknown };
    const id = typeof row.id === 'string' && row.id.trim() ? row.id.trim() : `cat_${idx}`;
    const label = typeof row.label === 'string' && row.label.trim() ? row.label.trim() : id;
    const order = Number.isFinite(Number(row.order)) ? Number(row.order) : idx;
    const appIds = Array.isArray(row.appIds)
      ? row.appIds.map((appId) => String(appId || '').trim()).filter(Boolean)
      : [];
    normalized.push({ id, label, order, appIds });
  });
  if (normalized.length === 0) return fallback;
  return normalized;
};

const normalizeAppLayoutProfiles = (raw: unknown): AppLayoutProfilesConfig => {
  const fallback = createDefaultAppLayoutProfiles();
  if (!raw || typeof raw !== 'object') return fallback;
  const payload = raw as {
    categories?: unknown;
    desktop?: { categories?: unknown } | null;
    mobile?: { categories?: unknown } | null;
  };
  if (Array.isArray(payload.categories)) {
    const categories = normalizeAppLayoutCategories(payload.categories, DEFAULT_DESKTOP_APP_LAYOUT_CATEGORIES);
    return {
      desktop: { categories: cloneAppLayoutCategories(categories) },
      mobile: { categories: cloneAppLayoutCategories(categories) },
    };
  }
  return {
    desktop: {
      categories: normalizeAppLayoutCategories(payload.desktop?.categories, DEFAULT_DESKTOP_APP_LAYOUT_CATEGORIES),
    },
    mobile: {
      categories: normalizeAppLayoutCategories(payload.mobile?.categories, DEFAULT_MOBILE_APP_LAYOUT_CATEGORIES),
    },
  };
};

const isSameAppIdOrder = (actual: string[], expected: string[]): boolean => (
  actual.length === expected.length && actual.every((id, idx) => id === expected[idx])
);

const lockHomeLayoutCategories = (
  incoming: AppLayoutCategoryConfig[],
  fallback: AppLayoutCategoryConfig[],
): AppLayoutCategoryConfig[] => {
  const fallbackMap = new Map(fallback.map((item) => [item.id, item] as const));
  const incomingMap = new Map(incoming.map((item) => [item.id, item] as const));

  return LOCKED_HOME_LAYOUT_ORDER.map((id, index) => {
    const fb = fallbackMap.get(id);
    if (!fb) return { id, label: LOCKED_HOME_LAYOUT_LABELS[id], order: index, appIds: [] };

    const inc = incomingMap.get(id);
    if (!inc) {
      return { ...fb, label: LOCKED_HOME_LAYOUT_LABELS[id], order: index, appIds: [...fb.appIds] };
    }

    const normalizedIncomingIds = Array.isArray(inc.appIds) ? inc.appIds.map((x) => String(x || '').trim()).filter(Boolean) : [];
    const labelMatch = String(inc.label || '').trim() === LOCKED_HOME_LAYOUT_LABELS[id];
    const appIdsMatch = isSameAppIdOrder(normalizedIncomingIds, fb.appIds);
    if (!labelMatch || !appIdsMatch) {
      return { ...fb, label: LOCKED_HOME_LAYOUT_LABELS[id], order: index, appIds: [...fb.appIds] };
    }
    return {
      id,
      label: LOCKED_HOME_LAYOUT_LABELS[id],
      order: index,
      appIds: [...normalizedIncomingIds],
    };
  });
};

const lockHomeLayoutProfiles = (profiles: AppLayoutProfilesConfig): AppLayoutProfilesConfig => ({
  desktop: {
    categories: lockHomeLayoutCategories(
      profiles.desktop.categories,
      DEFAULT_DESKTOP_APP_LAYOUT_CATEGORIES,
    ),
  },
  mobile: {
    categories: lockHomeLayoutCategories(
      profiles.mobile.categories,
      DEFAULT_MOBILE_APP_LAYOUT_CATEGORIES,
    ),
  },
});

const clearPanSessionCache = (): void => {
  try {
    sessionStorage.removeItem(PAN_SESSION_KEY);
    sessionStorage.removeItem(PAN_SESSION_RESTORE_ONCE_KEY);
  } catch {
    // noop
  }
};

interface InitialRouteState {
  viewMode: ViewMode;
  mobileTab: MobileTab;
  activeCategory: CategoryType;
  currentTool: ToolType | null;
  returnToPanFromTool: boolean;
}

const resolveInitialRouteState = (): InitialRouteState => {
  const pathTool = resolveStudyToolFromPath(window.location.pathname);
  const runtimeHost = getRuntimeHost();
  const q = new URLSearchParams(window.location.search);
  const queryCategory = q.get('cat');
  const queryTool = q.get('tool');
  const queryFrom = q.get('from');
  const queryTab = q.get('tab');
  const storedUser = getStoredUser();
  const xiaoyaoWenceUnlocked = loadXiaoyaoWenceUnlocked(storedUser?.userId);
  const validCategory = CATEGORIES.find((c) => c.id === queryCategory)?.id || null;
  const validTool = [
    ...TOOLS.map((t) => t.id),
    ...OTHER_APPS.map((t) => t.id),
    ...PSYCH_APPS.map((p) => p.id),
    'theory',
    'originMemory',
    'xuanxueStories',
    'pan',
    'dalurenPan',
  ].find((id) => (
    id === queryTool
    && (id !== 'xiaoyaoWence' || xiaoyaoWenceUnlocked)
    && isToolAllowedForHost(id, runtimeHost)
    && !STRICT_MEMBER_TRIAL_TOOL_IDS.has(id as typeof OTHER_APPS[number]['id'])
  )) as ToolType | undefined;

  if (validTool) {
    return {
      viewMode: 'sidebar_view',
      mobileTab: 'practice',
      activeCategory: 'stems',
      currentTool: validTool,
      returnToPanFromTool: queryFrom === 'pan',
    };
  }
  if (pathTool && isToolAllowedForHost(pathTool, runtimeHost)) {
    return {
      viewMode: 'sidebar_view',
      mobileTab: 'practice',
      activeCategory: 'stems',
      currentTool: pathTool,
      returnToPanFromTool: false,
    };
  }
  if (validCategory) {
    return {
      viewMode: 'sidebar_view',
      mobileTab: 'practice',
      activeCategory: validCategory,
      currentTool: null,
      returnToPanFromTool: false,
    };
  }
  if (!queryTab) {
    const launchTarget = normalizeLaunchTargetForHost(loadDefaultLaunchTarget(), runtimeHost);
    const launchTool = resolveLaunchToolFromTarget(launchTarget);
    if (launchTool && isToolAllowedForHost(launchTool, runtimeHost)) {
      return {
        viewMode: 'sidebar_view',
        mobileTab: 'practice',
        activeCategory: 'stems',
        currentTool: launchTool,
        returnToPanFromTool: false,
      };
    }
  }
  return {
    viewMode: 'dashboard',
    mobileTab: getInitialMobileTab(),
    activeCategory: 'stems',
    currentTool: null,
    returnToPanFromTool: false,
  };
};

const App: React.FC = () => {
  const runtimeHost = getRuntimeHost();
  const qimenOnlyMode = isQimenOnlyHost(runtimeHost);
  const disableHistoryBackGuardRef = useRef<boolean>(false);
  const edgeSwipeStartRef = useRef<{ x: number; y: number; edge: 'left' | 'right' | null; time: number }>({
    x: 0,
    y: 0,
    edge: null,
    time: 0,
  });
  const dashboardScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const dashboardScrollByTabRef = useRef<Record<MobileTab, number>>({
    practice: 0,
    study: 0,
    mine: 0,
  });
  const shouldRestoreDashboardScrollRef = useRef<boolean>(false);
  const sidebarContentScrollRef = useRef<HTMLDivElement | null>(null);
  const initialLaunchTargetRef = useRef<LaunchTarget>(
    normalizeLaunchTargetForHost(loadDefaultLaunchTarget(), runtimeHost),
  );
  const initialRouteStateRef = useRef<InitialRouteState>(resolveInitialRouteState());
  const toolUsageTrackTsRef = useRef<Record<string, number>>({});
  const analyticsSessionRef = useRef<{
    userId: string;
    sessionId: string;
    openedAt: number;
    activeToolId: ToolType | null;
    activeToolEnteredAt: number;
    appCloseSent: boolean;
  }>({
    userId: '',
    sessionId: '',
    openedAt: 0,
    activeToolId: null,
    activeToolEnteredAt: 0,
    appCloseSent: false,
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() => initialRouteStateRef.current.viewMode);
  const [mobileTab, setMobileTab] = useState<MobileTab>(() => initialRouteStateRef.current.mobileTab);
  const [activeCategory, setActiveCategory] = useState<CategoryType>(() => initialRouteStateRef.current.activeCategory);
  const [currentTool, setCurrentTool] = useState<ToolType | null>(() => initialRouteStateRef.current.currentTool);
  const [returnToPanFromTool, setReturnToPanFromTool] = useState<boolean>(() => initialRouteStateRef.current.returnToPanFromTool);
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [lockedToolName, setLockedToolName] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [showActivateInput, setShowActivateInput] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationResult, setActivationResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [defaultLaunchTarget, setDefaultLaunchTarget] = useState<LaunchTarget>(() => initialLaunchTargetRef.current);
  const [panDockTarget, setPanDockTarget] = useState<PanDockTarget>(() => (
    normalizePanDockTargetForHost(loadPanDockTarget(), runtimeHost)
  ));
  const [fourXiangyiUnlocked, setFourXiangyiUnlocked] = useState<boolean>(() => {
    const stored = getStoredUser();
    return loadFourXiangyiUnlocked(stored?.userId);
  });
  const [xiaoyaoWenceUnlocked, setXiaoyaoWenceUnlocked] = useState<boolean>(() => {
    const stored = getStoredUser();
    return loadXiaoyaoWenceUnlocked(stored?.userId);
  });
  const [namingMasterUnlocked, setNamingMasterUnlocked] = useState<boolean>(() => {
    const stored = getStoredUser();
    return loadNamingMasterUnlocked(stored?.userId);
  });
  const [defaultPanMode, setDefaultPanMode] = useState<DefaultPanMode>(() => loadDefaultPanMode());
  const [remoteMonthAsSubject, setRemoteMonthAsSubject] = useState<boolean>(() => loadRemoteMonthAsSubject());
  const [xiangyiCategoryMode, setXiangyiCategoryMode] = useState<XiangyiCategoryMode>(() => loadXiangyiCategoryMode());
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => loadSidebarCollapsed());
  const [sidebarSectionsExpanded, setSidebarSectionsExpanded] = useState<Record<SidebarSectionId, boolean>>(() => loadSidebarSectionsExpanded());
  const [mobileAppCols, setMobileAppCols] = useState<3 | 4>(() => loadMobileAppCols());
  const [mobileDockStyle, setMobileDockStyle] = useState<MobileDockStyle>(() => loadMobileDockStyle());
  const [mineSubPage, setMineSubPage] = useState<MineSubPage>('home');
  const [archiveToolState, setArchiveToolState] = useState<ArchiveToolState | null>(null);
  const [activeNavTap, setActiveNavTap] = useState<'pan' | MobileTab | null>(null);
  const navTapTimerRef = useRef<number | null>(null);
  const [accountNameDraft, setAccountNameDraft] = useState('');
  const [accountEmailDraft, setAccountEmailDraft] = useState('');
  const [accountPhoneDraft, setAccountPhoneDraft] = useState('');
  const [accountSaveResult, setAccountSaveResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [clientLogSubmitting, setClientLogSubmitting] = useState(false);
  const [clientLogSubmitResult, setClientLogSubmitResult] = useState<ClientLogSubmitResult | null>(null);
  const [showFeatureRemaining, setShowFeatureRemaining] = useState(false);
  const [featureRemainingLoading, setFeatureRemainingLoading] = useState(false);
  const [featureRemainingError, setFeatureRemainingError] = useState('');
  const [featureRemainingSummary, setFeatureRemainingSummary] = useState<FeatureRemainingSummary | null>(null);
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [teacherModalTitle, setTeacherModalTitle] = useState('联系客服');
  const [teacherModalDesc, setTeacherModalDesc] = useState('如有任何使用问题请扫码添加微信。');
  const [teacherModalMode, setTeacherModalMode] = useState<'contact' | 'notice'>('contact');
  const [appLayoutProfiles, setAppLayoutProfiles] = useState<AppLayoutProfilesConfig>(() => createDefaultAppLayoutProfiles());
  const [isDesktopViewport, setIsDesktopViewport] = useState<boolean>(() => detectDesktopViewport());
  const [isPadViewport, setIsPadViewport] = useState<boolean>(() => detectPadViewport());

  const toggleSidebarSection = (id: SidebarSectionId) => {
    setSidebarSectionsExpanded(prev => {
      const next = { ...prev, [id]: !prev[id] };
      saveSidebarSectionsExpanded(next);
      return next;
    });
  };
  const memberActive = Boolean(userProfile?.isMember || isProMember(userProfile as StoredUser | null));
  const normalizedWenceUserId = String(userProfile?.userId || userProfile?.email || '').trim().toLowerCase();
  const wenceProAccess = Boolean(
    userProfile
    && (
      userProfile.isAdminMember
      || userProfile.memberType === 'admin'
      || WENCE_PRO_BUILTIN_USERS.has(normalizedWenceUserId)
      || hasContentUnlock(WENCE_PRO_UNLOCK_ID, userProfile as StoredUser | null)
    )
  );
  const theoryProAccess = Boolean(memberActive || hasContentUnlock('theory', userProfile as StoredUser | null));
  const originMemoryProAccess = Boolean(memberActive || hasContentUnlock('originMemory', userProfile as StoredUser | null));
  const originMemoryProCategoryUnlocks = {
    doors: originMemoryProAccess || hasContentUnlock('originMemory:doors', userProfile as StoredUser | null),
    stars: originMemoryProAccess || hasContentUnlock('originMemory:stars', userProfile as StoredUser | null),
    gods: originMemoryProAccess || hasContentUnlock('originMemory:gods', userProfile as StoredUser | null),
    trigrams: originMemoryProAccess || hasContentUnlock('originMemory:trigrams', userProfile as StoredUser | null),
  } as const;
  const lockedToolTierLabel: 'PRO' | 'SVIP' = SVIP_TOOL_LABELS.has(String(lockedToolName || '').trim()) ? 'SVIP' : 'PRO';
  const membershipStatus = userProfile?.membershipStatus || (userProfile?.expireTime ? (memberActive ? '会员中' : '已过期') : '未开通');
  const expireTimeText = userProfile?.expireTime ? new Date(userProfile.expireTime).toLocaleString('zh-CN') : '未开通';
  const currentPointsText = `${Number.isFinite(userProfile?.currentPoints as number) ? userProfile?.currentPoints : 0} 分`;
  const featureRemainingSummaryText = (() => {
    if (featureRemainingLoading) return '加载中...';
    if (featureRemainingError) return '加载失败';
    if (!featureRemainingSummary) return '点击查看';
    if (featureRemainingSummary.unlimitedFeatureCount > 0) {
      return `${featureRemainingSummary.totalRemaining} 次 + 不限`;
    }
    return `${featureRemainingSummary.totalRemaining} 次`;
  })();

  const trackNonAiToolUsage = useCallback((toolId: ToolType) => {
    const rawId = String(toolId || '').trim();
    if (!rawId || !NON_AI_USAGE_TOOL_IDS.has(rawId)) return;
    const nowTs = Date.now();
    const lastTs = Number(toolUsageTrackTsRef.current[rawId] || 0);
    if (nowTs - lastTs < 5000) return;
    toolUsageTrackTsRef.current[rawId] = nowTs;
    const sessionMeta = analyticsSessionRef.current;
    void trackFeatureUsage({
      feature: toUsageFeatureId(rawId),
      featureName: TOOL_LABEL_MAP[rawId] || rawId,
      featureMode: 'open',
      eventType: 'tool_open',
      source: 'app',
      sessionId: sessionMeta.sessionId || undefined,
      pagePath: getCurrentPagePath(),
      clientTs: nowTs,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });
  }, []);

  const sendUsageBeacon = useCallback((payload: Record<string, unknown>) => {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      return navigator.sendBeacon(`${API_BASE}/usage/track`, blob);
    } catch {
      return false;
    }
  }, []);

  const ensureAnalyticsSession = useCallback((userId: string) => {
    const uid = String(userId || '').trim();
    if (!uid) return;
    const curr = analyticsSessionRef.current;
    if (curr.userId === uid && curr.sessionId) return;
    const nowTs = Date.now();
    const nextSessionId = buildClientSessionId();
    analyticsSessionRef.current = {
      userId: uid,
      sessionId: nextSessionId,
      openedAt: nowTs,
      activeToolId: curr.activeToolId,
      activeToolEnteredAt: curr.activeToolEnteredAt,
      appCloseSent: false,
    };
    void trackFeatureUsage({
      userId: uid,
      feature: 'app:session',
      featureName: '应用会话',
      featureMode: 'open',
      eventType: 'app_open',
      source: 'app',
      sessionId: nextSessionId,
      pagePath: getCurrentPagePath(),
      clientTs: nowTs,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });
  }, []);

  const flushActiveToolLeave = useCallback((reason: 'switch' | 'close') => {
    const state = analyticsSessionRef.current;
    const uid = String(state.userId || '').trim();
    const toolId = state.activeToolId;
    const enteredAt = Number(state.activeToolEnteredAt || 0);
    if (!uid || !toolId || enteredAt <= 0) return;
    const nowTs = Date.now();
    const durationMs = Math.max(0, nowTs - enteredAt);
    const payload = {
      userId: uid,
      feature: toUsageFeatureId(toolId),
      featureName: TOOL_LABEL_MAP[toolId] || toolId,
      featureMode: reason === 'close' ? 'close' : 'leave',
      eventType: 'tool_leave',
      source: 'app',
      durationMs,
      sessionId: state.sessionId || undefined,
      pagePath: getCurrentPagePath(),
      clientTs: nowTs,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    };
    if (reason === 'close') {
      if (!sendUsageBeacon(payload)) {
        void trackFeatureUsage(payload);
      }
    } else {
      void trackFeatureUsage(payload);
    }
    analyticsSessionRef.current.activeToolId = null;
    analyticsSessionRef.current.activeToolEnteredAt = 0;
  }, [sendUsageBeacon]);

  useEffect(() => {
    const stored = getStoredUser();
    if (stored?.userId) {
      setUserProfile(stored);
      setIsLoggedIn(true);
      setFourXiangyiUnlocked(loadFourXiangyiUnlocked(stored.userId));
      setXiaoyaoWenceUnlocked(loadXiaoyaoWenceUnlocked(stored.userId));
      if (stored?.userId) {
        void refreshStoredUserFromServer().then((latest) => {
          if (!latest?.userId) return;
          setUserProfile(latest);
          setIsLoggedIn(true);
          setFourXiangyiUnlocked(loadFourXiangyiUnlocked(latest.userId));
          setXiaoyaoWenceUnlocked(loadXiaoyaoWenceUnlocked(latest.userId));
        });
      }
    }
  }, []);

  useEffect(() => {
    const uid = isLoggedIn && userProfile?.userId ? String(userProfile.userId) : '';
    if (!uid) return;
    ensureAnalyticsSession(uid);
  }, [isLoggedIn, userProfile?.userId, ensureAnalyticsSession]);

  useEffect(() => {
    if (isLoggedIn && userProfile?.userId) return;
    const state = analyticsSessionRef.current;
    if (state.userId && state.sessionId && !state.appCloseSent) {
      const nowTs = Date.now();
      const durationMs = state.openedAt > 0 ? Math.max(0, nowTs - state.openedAt) : 0;
      void trackFeatureUsage({
        userId: state.userId,
        feature: 'app:session',
        featureName: '应用会话',
        featureMode: 'logout',
        eventType: 'app_close',
        source: 'app',
        durationMs,
        sessionId: state.sessionId,
        pagePath: getCurrentPagePath(),
        clientTs: nowTs,
        referrer: typeof document !== 'undefined' ? document.referrer : '',
      });
    }
    analyticsSessionRef.current = {
      userId: '',
      sessionId: '',
      openedAt: 0,
      activeToolId: null,
      activeToolEnteredAt: 0,
      appCloseSent: false,
    };
  }, [isLoggedIn, userProfile?.userId]);

  useEffect(() => {
    const uid = isLoggedIn && userProfile?.userId ? String(userProfile.userId) : '';
    if (!uid) return;
    ensureAnalyticsSession(uid);
    const state = analyticsSessionRef.current;
    const prevTool = state.activeToolId;
    if (prevTool && prevTool !== currentTool) {
      flushActiveToolLeave('switch');
    }

    if (!currentTool) {
      analyticsSessionRef.current.activeToolId = null;
      analyticsSessionRef.current.activeToolEnteredAt = 0;
      return;
    }

    if (analyticsSessionRef.current.activeToolId !== currentTool || analyticsSessionRef.current.activeToolEnteredAt <= 0) {
      const nowTs = Date.now();
      analyticsSessionRef.current.activeToolId = currentTool;
      analyticsSessionRef.current.activeToolEnteredAt = nowTs;
      void trackFeatureUsage({
        userId: uid,
        feature: toUsageFeatureId(currentTool),
        featureName: TOOL_LABEL_MAP[currentTool] || currentTool,
        featureMode: 'enter',
        eventType: 'tool_enter',
        source: 'app',
        sessionId: analyticsSessionRef.current.sessionId || undefined,
        pagePath: getCurrentPagePath(),
        clientTs: nowTs,
        referrer: typeof document !== 'undefined' ? document.referrer : '',
      });
    }
  }, [currentTool, isLoggedIn, userProfile?.userId, ensureAnalyticsSession, flushActiveToolLeave]);

  useEffect(() => {
    const flushAppClose = () => {
      const state = analyticsSessionRef.current;
      if (!state.userId || !state.sessionId || state.appCloseSent) return;
      flushActiveToolLeave('close');
      const nowTs = Date.now();
      const durationMs = state.openedAt > 0 ? Math.max(0, nowTs - state.openedAt) : 0;
      const payload = {
        userId: state.userId,
        feature: 'app:session',
        featureName: '应用会话',
        featureMode: 'close',
        eventType: 'app_close',
        source: 'app',
        durationMs,
        sessionId: state.sessionId,
        pagePath: getCurrentPagePath(),
        clientTs: nowTs,
        referrer: typeof document !== 'undefined' ? document.referrer : '',
      };
      if (!sendUsageBeacon(payload)) {
        void trackFeatureUsage(payload);
      }
      analyticsSessionRef.current.appCloseSent = true;
    };

    window.addEventListener('pagehide', flushAppClose);
    window.addEventListener('beforeunload', flushAppClose);
    return () => {
      window.removeEventListener('pagehide', flushAppClose);
      window.removeEventListener('beforeunload', flushAppClose);
    };
  }, [flushActiveToolLeave, sendUsageBeacon]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 7000);
    fetch(`${API_BASE}/api/app-layout`, { signal: controller.signal })
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        // 防止远端旧配置在首屏后覆盖当前锁定版首页结构，导致“闪回旧版”。
        const normalized = normalizeAppLayoutProfiles(payload);
        setAppLayoutProfiles(lockHomeLayoutProfiles(normalized));
      })
      .catch(() => {
        // fallback to local defaults when backend layout is unavailable
      })
      .finally(() => {
        window.clearTimeout(timer);
      });
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      setIsDesktopViewport(detectDesktopViewport());
      setIsPadViewport(detectPadViewport());
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !userProfile?.userId) {
      setFourXiangyiUnlocked(false);
      setXiaoyaoWenceUnlocked(false);
      setNamingMasterUnlocked(false);
      return;
    }
    setFourXiangyiUnlocked(loadFourXiangyiUnlocked(userProfile.userId));
    setXiaoyaoWenceUnlocked(loadXiaoyaoWenceUnlocked(userProfile.userId));
    setNamingMasterUnlocked(loadNamingMasterUnlocked(userProfile.userId));
  }, [isLoggedIn, userProfile?.userId]);

  useEffect(() => {
    if (currentTool === 'xiaoyaoWence' && !xiaoyaoWenceUnlocked) {
      setCurrentTool(null);
      setReturnToPanFromTool(false);
      setViewMode('dashboard');
      replaceCurrentUrlQuery(null, '/');
    }
  }, [currentTool, xiaoyaoWenceUnlocked]);

  useEffect(() => {
    if (currentTool === 'namingMaster' && !namingMasterUnlocked) {
      setCurrentTool(null);
      setReturnToPanFromTool(false);
      setViewMode('dashboard');
      replaceCurrentUrlQuery(null, '/');
    }
  }, [currentTool, namingMasterUnlocked]);

  useEffect(() => {
    if (!isLoggedIn || !userProfile) {
      setAccountNameDraft('');
      setAccountEmailDraft('');
      setAccountPhoneDraft('');
      setAccountSaveResult(null);
      setClientLogSubmitting(false);
      setClientLogSubmitResult(null);
      setMineSubPage('home');
      return;
    }
    setAccountNameDraft(String(userProfile.name || ''));
    setAccountEmailDraft(String(userProfile.email || ''));
    setAccountPhoneDraft(String(userProfile.phone || ''));
  }, [isLoggedIn, userProfile?.name, userProfile?.email, userProfile?.phone]);

  useEffect(() => {
    if (mobileTab !== 'mine') {
      setMineSubPage('home');
      setAccountSaveResult(null);
      setClientLogSubmitResult(null);
    }
  }, [mobileTab]);

  useEffect(() => {
    setArchiveToolState((prev) => {
      if (!prev) return prev;
      if (!currentTool) return null;
      return prev.tool === currentTool ? prev : null;
    });
  }, [currentTool]);

  useEffect(() => {
    if (!showAuthModal) {
      setMineSubPage('home');
      setAccountSaveResult(null);
      setClientLogSubmitResult(null);
    }
  }, [showAuthModal]);

  useEffect(() => {
    setShowFeatureRemaining(false);
    setFeatureRemainingError('');
    setFeatureRemainingSummary(null);
  }, [userProfile?.userId]);

  useEffect(() => {
    const timer = setInterval(() => {
      const latest = getStoredUser();
      if (!latest?.userId) return;
      setUserProfile((prev) => {
        if (!prev) return latest;
        if (
          prev.currentPoints !== latest.currentPoints
          || prev.expireTime !== latest.expireTime
          || prev.membershipStatus !== latest.membershipStatus
          || prev.memberType !== latest.memberType
          || prev.isAdminMember !== latest.isAdminMember
          || prev.isMember !== latest.isMember
          || JSON.stringify(prev.contentUnlocks || {}) !== JSON.stringify(latest.contentUnlocks || {})
        ) {
          return latest;
        }
        return prev;
      });
    }, 1200);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    clearStoredUser();
    setIsLoggedIn(false);
    setUserProfile(null);
    setMineSubPage('home');
    setAccountNameDraft('');
    setAccountEmailDraft('');
    setAccountPhoneDraft('');
    setAccountSaveResult(null);
    setClientLogSubmitting(false);
    setClientLogSubmitResult(null);
    setFourXiangyiUnlocked(false);
    setXiaoyaoWenceUnlocked(false);
    setNamingMasterUnlocked(false);
    setShowFeatureRemaining(false);
    setFeatureRemainingLoading(false);
    setFeatureRemainingError('');
    setFeatureRemainingSummary(null);
    setActivationCode('');
    setActivationResult(null);
  };

  const handleLoginSuccess = (user: StoredUser) => {
    setIsLoggedIn(true);
    setUserProfile(user);
    setFourXiangyiUnlocked(loadFourXiangyiUnlocked(user.userId));
    setXiaoyaoWenceUnlocked(loadXiaoyaoWenceUnlocked(user.userId));
    setNamingMasterUnlocked(loadNamingMasterUnlocked(user.userId));
    setShowFeatureRemaining(false);
    setFeatureRemainingError('');
    setFeatureRemainingSummary(null);
    setShowAuthModal(false);
    setActivationResult(null);
  };

  const openProModal = (toolName: string) => {
    setLockedToolName(toolName);
    setActivationResult(null);
    setShowProModal(true);
  };

  const handleEnterProTool = (toolId: typeof TOOLS[number]['id']) => {
    if (!isToolAllowedForHost(toolId, runtimeHost)) return;
    const target = TOOLS.find((t) => t.id === toolId);
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!memberActive) {
      openProModal(target?.label || '该功能');
      return;
    }
    enterToolMode(toolId);
  };

  const ensureTrialAvailable = async (toolId: ToolType, toolLabel: string): Promise<boolean> => {
    if (toolId === 'sancaiLiuge' && !memberActive) {
      openProModal(toolLabel);
      return false;
    }
    if (STRICT_MEMBER_TRIAL_TOOL_IDS.has(toolId as typeof OTHER_APPS[number]['id'])) {
      if (memberActive) return true;
      const uid = String(userProfile?.userId || getStoredUser()?.userId || '').trim();
      if (!uid) {
        setShowAuthModal(true);
        return false;
      }
      const trial = await checkFeatureTrialAccess({
        userId: uid,
        feature: String(toolId),
        consume: true,
      });
      if (trial?.allowed) return true;
      openProModal(toolLabel);
      setActivationResult({ ok: false, msg: `${toolLabel}免费试用次数已用完，请添加轩哥微信获取激活码。` });
      return false;
    }
    if (!LIMITED_TRIAL_TOOL_IDS.has(toolId)) return true;
    if (memberActive) return true;
    const uid = userProfile?.userId || getStoredUser()?.userId || '';
    const used = loadToolTrialUsage(toolId, uid);
    if (used >= TOOL_TRIAL_LIMIT) {
      setTeacherModalTitle(toolLabel);
      setTeacherModalDesc('该功能试用次数已用完，需成为会员，请扫码添加若轩老师微信获取激活二维码。');
      setTeacherModalMode('contact');
      setTeacherModalOpen(true);
      return false;
    }
    saveToolTrialUsage(toolId, used + 1, uid);
    return true;
  };
  const handleEnterOtherApp = async (toolId: typeof OTHER_APPS[number]['id']) => {
    if (!isToolAllowedForHost(toolId, runtimeHost)) return;
    if (toolId === 'consultTeacher') {
      setTeacherModalTitle('咨询老师');
      setTeacherModalDesc('请添加若轩老师微信，扫码后可咨询开通与使用问题。');
      setTeacherModalMode('contact');
      setTeacherModalOpen(true);
      return;
    }
    const target = OTHER_APPS.find((a) => a.id === toolId);
    if (LOCKED_OTHER_APP_IDS.has(toolId)) {
      if (!isLoggedIn) {
        setShowAuthModal(true);
        return;
      }
      const hasAccess = (toolId === 'wence' || toolId === 'xiaoyaoWence')
        ? wenceProAccess
        : memberActive;
      if (!hasAccess) {
        openProModal(target?.label || '该功能');
        return;
      }
    }
    if (!(await ensureTrialAvailable(toolId, target?.label || '该功能'))) {
      return;
    }
    enterToolMode(toolId);
  };

  const handleActivateSubmit = async () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    const codeText = activationCode.trim();
    if (!codeText) {
      setActivationResult({ ok: false, msg: '请输入激活码' });
      return;
    }

    if (codeText === FOUR_XIANGYI_UNLOCK_CODE) {
      saveFourXiangyiUnlocked(userProfile?.userId, true);
      setFourXiangyiUnlocked(true);
      saveXiaoyaoWenceUnlocked(userProfile?.userId, true);
      setXiaoyaoWenceUnlocked(true);
      setActivationCode('');
      setActivationResult({ ok: true, msg: '已开启“逍遥问测”入口。' });
      setShowActivateInput(false);
      return;
    }

    if (codeText === NAMING_MASTER_UNLOCK_CODE) {
      saveNamingMasterUnlocked(userProfile?.userId, true);
      setNamingMasterUnlocked(true);
      setActivationCode('');
      setActivationResult({ ok: true, msg: '已开启“起名大师”入口。' });
      setShowActivateInput(false);
      return;
    }

    setActivationLoading(true);
    setActivationResult(null);
    try {
      const updated = await activateMembershipCode(codeText);
      setUserProfile(updated);
      setActivationCode('');
      setActivationResult({ ok: true, msg: '激活成功，权益已更新' });
      setShowActivateInput(false);
      setShowProModal(false);
      setTeacherModalTitle('激活成功');
      setTeacherModalDesc('激活成功，权益已更新。');
      setTeacherModalMode('notice');
      setTeacherModalOpen(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '激活失败';
      setActivationResult({ ok: false, msg });
    } finally {
      setActivationLoading(false);
    }
  };

  const copyWechat = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(WECHAT_ID);
      } else {
        const temp = document.createElement('input');
        temp.value = WECHAT_ID;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setActivationResult({ ok: true, msg: '微信号已复制' });
    } catch (_) {
      setActivationResult({ ok: false, msg: '复制失败，请手动添加微信号 dao6257' });
    }
  };

  const handleDefaultPanModeChange = (mode: DefaultPanMode) => {
    setDefaultPanMode(mode);
    saveDefaultPanMode(mode);
  };

  const handleDefaultLaunchTargetChange = (target: LaunchTarget) => {
    const next = normalizeLaunchTargetForHost(normalizeDefaultLaunchTarget(target), runtimeHost);
    setDefaultLaunchTarget(next);
    saveDefaultLaunchTarget(next);
  };

  const handlePanDockTargetChange = (target: PanDockTarget) => {
    const next = normalizePanDockTargetForHost(normalizePanDockTarget(target), runtimeHost);
    setPanDockTarget(next);
    savePanDockTarget(next);
  };

  useEffect(() => {
    const normalized = normalizeLaunchTargetForHost(defaultLaunchTarget, runtimeHost);
    if (normalized === defaultLaunchTarget) return;
    setDefaultLaunchTarget(normalized);
    saveDefaultLaunchTarget(normalized);
  }, [defaultLaunchTarget, runtimeHost]);

  useEffect(() => {
    const normalized = normalizePanDockTargetForHost(panDockTarget, runtimeHost);
    if (normalized === panDockTarget) return;
    setPanDockTarget(normalized);
    savePanDockTarget(normalized);
  }, [panDockTarget, runtimeHost]);

  const handleMobileAppColsChange = (cols: 3 | 4) => {
    setMobileAppCols(cols);
    saveMobileAppCols(cols);
  };

  const handleMobileDockStyleChange = (style: MobileDockStyle) => {
    setMobileDockStyle(style);
    saveMobileDockStyle(style);
  };

  useEffect(() => () => {
    if (navTapTimerRef.current) {
      window.clearTimeout(navTapTimerRef.current);
      navTapTimerRef.current = null;
    }
  }, []);

  const triggerNavTapEffect = useCallback((tab: 'pan' | MobileTab) => {
    if (navTapTimerRef.current) {
      window.clearTimeout(navTapTimerRef.current);
      navTapTimerRef.current = null;
    }
    setActiveNavTap(null);
    window.requestAnimationFrame(() => {
      setActiveNavTap(tab);
      navTapTimerRef.current = window.setTimeout(() => {
        setActiveNavTap((prev) => (prev === tab ? null : prev));
        navTapTimerRef.current = null;
      }, 420);
    });
  }, []);

  const handleRemoteMonthAsSubjectChange = (value: boolean) => {
    setRemoteMonthAsSubject(value);
    saveRemoteMonthAsSubject(value);
  };

  const loadFeatureRemaining = useCallback(async () => {
    setFeatureRemainingLoading(true);
    setFeatureRemainingError('');
    try {
      const summary = await fetchFeatureRemainingSummary();
      setFeatureRemainingSummary(summary);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '获取剩余次数失败';
      setFeatureRemainingError(msg);
      setFeatureRemainingSummary(null);
    } finally {
      setFeatureRemainingLoading(false);
    }
  }, []);

  const handleToggleFeatureRemaining = () => {
    const next = !showFeatureRemaining;
    setShowFeatureRemaining(next);
    if (next) {
      void loadFeatureRemaining();
    }
  };

  const handleSubmitClientLog = async () => {
    const user = getStoredUser();
    const userId = String(userProfile?.userId || user?.userId || '').trim();
    if (!userId) {
      setClientLogSubmitResult({ ok: false, msg: '请先登录后再提交日志' });
      return;
    }

    setClientLogSubmitting(true);
    setClientLogSubmitResult(null);
    try {
      const localSnapshots = CLIENT_LOG_LOCAL_STORAGE_KEYS
        .map((key) => readStorageSnapshot(typeof window !== 'undefined' ? window.localStorage : null, key))
        .filter((item): item is Record<string, unknown> => Boolean(item));
      const sessionSnapshots = CLIENT_LOG_SESSION_STORAGE_KEYS
        .map((key) => readStorageSnapshot(typeof window !== 'undefined' ? window.sessionStorage : null, key))
        .filter((item): item is Record<string, unknown> => Boolean(item));
      const localStorageRef = typeof window !== 'undefined' ? window.localStorage : null;
      const payload = {
        submittedAt: new Date().toISOString(),
        from: 'account-settings',
        user: {
          userId,
          name: String(userProfile?.name || user?.name || ''),
          email: String(userProfile?.email || user?.email || ''),
          memberType: String(userProfile?.memberType || user?.memberType || ''),
          membershipStatus: String(userProfile?.membershipStatus || user?.membershipStatus || ''),
        },
        page: {
          path: getCurrentPagePath(),
          href: typeof window !== 'undefined' ? String(window.location.href || '') : '',
        },
        runtime: {
          userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 320) : '',
          language: typeof navigator !== 'undefined' ? String(navigator.language || '') : '',
          online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
          apiBase: API_BASE,
        },
        storage: {
          local: localSnapshots,
          session: sessionSnapshots,
        },
        recordCounts: {
          qimenPan: readStorageArrayCount(localStorageRef, 'qimen_pan_records_v2'),
          dalurenPan: readStorageArrayCount(localStorageRef, 'daluren_pan_records_v1'),
          sancai: readStorageArrayCount(localStorageRef, 'qimen_sancai_liuge_records_v1'),
          wenceHistory: readStorageArrayCount(localStorageRef, 'qimen_wence_history_v1'),
          xiaoyaoWenceHistory: readStorageArrayCount(localStorageRef, 'qimen_xiaoyao_wence_history_v1'),
        },
      };

      const result = await submitClientLog({
        userId,
        source: 'account-settings',
        category: 'diagnostic',
        appLabel: APP_NAME,
        message: '用户在账号设置页手动提交诊断日志',
        payload,
      });
      setClientLogSubmitResult({
        ok: true,
        msg: result.truncated
          ? `日志已提交（内容较大已截断），编号：${result.logId}`
          : `日志已提交，编号：${result.logId}`,
        logId: result.logId,
      });
    } catch (err) {
      setClientLogSubmitResult({ ok: false, msg: err instanceof Error ? err.message : '提交日志失败' });
    } finally {
      setClientLogSubmitting(false);
    }
  };

  const openMineSubPage = (page: MineSubPage) => {
    setMineSubPage(page);
    setAccountSaveResult(null);
    setClientLogSubmitResult(null);
  };

  const handleSaveAccountSettings = () => {
    const nextName = String(accountNameDraft || '').trim();
    const nextEmail = String(accountEmailDraft || '').trim();
    const nextPhone = String(accountPhoneDraft || '').trim();
    if (!nextName) {
      setAccountSaveResult({ ok: false, msg: '昵称不能为空' });
      return;
    }
    if (nextEmail && !nextEmail.includes('@')) {
      setAccountSaveResult({ ok: false, msg: '邮箱格式不正确' });
      return;
    }
    const phoneDigits = nextPhone.replace(/\D+/g, '');
    if (nextPhone && phoneDigits.length < 6) {
      setAccountSaveResult({ ok: false, msg: '手机号格式不正确' });
      return;
    }

    setUserProfile((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        name: nextName,
        email: nextEmail || prev.email || prev.userId,
        phone: nextPhone,
      };
    });

    const stored = getStoredUser();
    if (stored?.userId) {
      saveStoredUser({
        ...stored,
        name: nextName,
        email: nextEmail || stored.email || stored.userId,
        phone: nextPhone,
      });
    }

    setAccountSaveResult({ ok: true, msg: '账号设置已保存' });
  };

  const replaceCurrentUrlQuery = (params?: URLSearchParams | null, pathOverride?: string | null) => {
    const path = normalizeAppPathname(pathOverride || window.location.pathname || '/');
    const hasParams = Boolean(params && Array.from(params.keys()).length > 0);
    const url = hasParams ? `${path}?${params?.toString()}` : path;
    const state = (window.history.state && typeof window.history.state === 'object')
      ? window.history.state
      : null;
    window.history.replaceState(state, '', url);
  };

  const captureDashboardScrollPosition = useCallback(() => {
    const node = dashboardScrollContainerRef.current;
    if (!node) return;
    dashboardScrollByTabRef.current[mobileTab] = node.scrollTop;
  }, [mobileTab]);

  const handleDashboardScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    dashboardScrollByTabRef.current[mobileTab] = event.currentTarget.scrollTop;
  }, [mobileTab]);

  const scrollSidebarContentToTop = useCallback(() => {
    if (!isDesktopViewport) return;
    const node = sidebarContentScrollRef.current;
    if (!node) return;
    node.scrollTop = 0;
  }, [isDesktopViewport]);

  useEffect(() => {
    if (viewMode !== 'dashboard') return;
    if (!shouldRestoreDashboardScrollRef.current) return;
    const restoreTop = dashboardScrollByTabRef.current[mobileTab] || 0;
    const raf = window.requestAnimationFrame(() => {
      const node = dashboardScrollContainerRef.current;
      if (!node) return;
      node.scrollTop = restoreTop;
      shouldRestoreDashboardScrollRef.current = false;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [viewMode, mobileTab]);

  useLayoutEffect(() => {
    if (viewMode !== 'sidebar_view') return;
    if (!isDesktopViewport) return;
    scrollSidebarContentToTop();
  }, [viewMode, currentTool, activeCategory, isDesktopViewport, scrollSidebarContentToTop]);


  const getCategoryData = () => {
     switch(activeCategory) {
         case 'stems': return stemsData;
         case 'stem_combinations': return stemCombinationsData;
         case 'doors': return doorsData;
         case 'gods': return godsData;
         case 'stars': return starsData;
         case 'trigrams': return trigramsData;
         default: return stemsData;
     }
  };

  const currentData = getCategoryData().filter(item => 
    item.summary.includes(searchQuery) || 
    item.char.includes(searchQuery) || 
    (item.tags && item.tags.some(t => t.includes(searchQuery)))
  );
  const activeLayoutCategories = (
    isDesktopViewport ? appLayoutProfiles.desktop.categories : appLayoutProfiles.mobile.categories
  ).slice().sort((a, b) => a.order - b.order);
  const fallbackCategoryMap = new Map(
    (isDesktopViewport ? DEFAULT_DESKTOP_APP_LAYOUT_CATEGORIES : DEFAULT_MOBILE_APP_LAYOUT_CATEGORIES)
      .map((cat) => [cat.id, cat] as const),
  );
  const pickLayoutCategory = (id: string, fallbackLabel: string, fallbackAppIds: string[]): AppLayoutCategoryConfig => {
    const fromLayout = activeLayoutCategories.find((cat) => cat.id === id);
    if (fromLayout) return fromLayout;
    const fallback = fallbackCategoryMap.get(id);
    if (fallback) return { ...fallback, appIds: [...fallback.appIds] };
    return { id, label: fallbackLabel, order: 999, appIds: [...fallbackAppIds] };
  };
  const practiceCategory = pickLayoutCategory('practice', '奇门学习', TOOLS.map((tool) => tool.id));
  const otherCategory = pickLayoutCategory('other', '专业排盘', ['pan', ...OTHER_APPS.map((app) => app.id)]);
  const psychCategory = pickLayoutCategory('psych', '心理测试', PSYCH_APPS.map((app) => app.id));
  const studyCategory = pickLayoutCategory('study', '学习中心', STUDY_CARD_IDS);
  const practiceCategoryLabel = String(practiceCategory.label || '奇门学习').trim() || '奇门学习';
  const otherCategoryLabel = String(otherCategory.label || '专业排盘').trim() || '专业排盘';
  const psychCategoryLabel = String(psychCategory.label || '心理测试').trim() || '心理测试';
  const studyCategoryLabel = String(studyCategory.label || '学习中心').trim() || '学习中心';
  const sectionOrder = {
    other: Number.isFinite(Number(otherCategory.order)) ? Number(otherCategory.order) : 0,
    practice: Number.isFinite(Number(practiceCategory.order)) ? Number(practiceCategory.order) : 1,
    study: Number.isFinite(Number(studyCategory.order)) ? Number(studyCategory.order) : 2,
    psych: Number.isFinite(Number(psychCategory.order)) ? Number(psychCategory.order) : 3,
  };
  const practiceOrderFromLayout = (practiceCategory.appIds || []).filter((id): id is typeof TOOLS[number]['id'] => (
    TOOL_ID_SET.has(id)
  ));
  const orderedPracticeToolIds: Array<typeof TOOLS[number]['id']> = [
    ...practiceOrderFromLayout,
    ...TOOLS.map((tool) => tool.id).filter((id) => !practiceOrderFromLayout.includes(id)),
  ];
  const orderedPracticeTools = orderedPracticeToolIds
    .map((id) => TOOLS.find((tool) => tool.id === id))
    .filter((tool): tool is typeof TOOLS[number] => Boolean(tool))
    .filter((tool) => !qimenOnlyMode || QIMEN_PRACTICE_TOOL_ID_SET.has(tool.id));
  const otherOrderFromLayout = (otherCategory.appIds || []).filter((id): id is typeof OTHER_APPS[number]['id'] => (
    OTHER_APP_ID_SET.has(id)
  ));
  const orderedOtherAppIds: Array<typeof OTHER_APPS[number]['id']> = [
    ...otherOrderFromLayout,
    ...OTHER_APPS.map((app) => app.id).filter((id) => !otherOrderFromLayout.includes(id)),
  ];
  const orderedOtherApps = orderedOtherAppIds
    .map((id) => OTHER_APPS.find((app) => app.id === id))
    .filter((app): app is typeof OTHER_APPS[number] => Boolean(app))
    .filter((app) => !qimenOnlyMode || QIMEN_OTHER_APP_ID_SET.has(app.id))
    .filter((app) => app.id !== 'namingMaster' || namingMasterUnlocked)
    .filter((app) => app.id !== 'xiaoyaoWence' || xiaoyaoWenceUnlocked);
  const psychOrderFromLayout = (psychCategory.appIds || []).filter((id): id is typeof PSYCH_APPS[number]['id'] => (
    PSYCH_APP_ID_SET.has(id)
  ));
  const orderedPsychAppIds: Array<typeof PSYCH_APPS[number]['id']> = [
    ...psychOrderFromLayout,
    ...PSYCH_APPS.map((app) => app.id).filter((id) => !psychOrderFromLayout.includes(id)),
  ];
  const orderedPsychApps = orderedPsychAppIds
    .map((id) => PSYCH_APPS.find((app) => app.id === id))
    .filter((app): app is typeof PSYCH_APPS[number] => Boolean(app));
  const studyOrderFromLayout = (studyCategory.appIds || []).filter((id): id is StudyCardId => (
    STUDY_CARD_IDS.includes(id as StudyCardId)
  ));
  const orderedStudyCardIds: StudyCardId[] = [
    ...studyOrderFromLayout,
    ...STUDY_CARD_IDS.filter((id) => !studyOrderFromLayout.includes(id)),
  ].filter((id) => !qimenOnlyMode || QIMEN_STUDY_CARD_ID_SET.has(id));
  const launchTargetOptions = DEFAULT_LAUNCH_TARGET_OPTIONS.filter((item) => (
    !qimenOnlyMode || QIMEN_ALLOWED_DEFAULT_LAUNCH_TARGET_SET.has(item.id)
  ));
  const panDockTargetOptions = PAN_DOCK_TARGET_OPTIONS.filter((item) => (
    !qimenOnlyMode || QIMEN_ALLOWED_PAN_DOCK_TARGET_SET.has(item.id)
  ));

  const enterLearnMode = (catId: CategoryType) => {
    if (PRO_LEARN_CATEGORY_IDS.includes(catId) && !memberActive) {
      const cat = CATEGORIES.find((c) => c.id === catId);
      openProModal(cat?.label || '该功能');
      return;
    }
    if (viewMode === 'dashboard') {
      captureDashboardScrollPosition();
      shouldRestoreDashboardScrollRef.current = true;
    }
    scrollSidebarContentToTop();
    setActiveCategory(catId);
    setCurrentTool(null); 
    setSelectedSymbol(null);
    setViewMode('sidebar_view');
    setSearchQuery('');
    setReturnToPanFromTool(false);
    replaceCurrentUrlQuery(null, '/');
  };

  const enterToolMode = (
    toolId: ToolType,
    options?: { fromPan?: boolean; queryParams?: URLSearchParams },
  ) => {
    if (!isToolAllowedForHost(toolId, runtimeHost)) {
      setCurrentTool(null);
      setSelectedSymbol(null);
      setViewMode('dashboard');
      setReturnToPanFromTool(false);
      replaceCurrentUrlQuery(null, '/');
      return;
    }
    if (toolId === 'namingMaster' && !namingMasterUnlocked) {
      setCurrentTool(null);
      setSelectedSymbol(null);
      setViewMode('dashboard');
      setReturnToPanFromTool(false);
      replaceCurrentUrlQuery(null, '/');
      return;
    }
    if (toolId === 'sancaiLiuge' && !memberActive) {
      openProModal('三才六格');
      return;
    }
    if (viewMode === 'dashboard') {
      captureDashboardScrollPosition();
      shouldRestoreDashboardScrollRef.current = true;
    }
    scrollSidebarContentToTop();
    const nextPath = resolveRoutePathForTool(toolId);
    setCurrentTool(toolId);
    trackNonAiToolUsage(toolId);
    setSelectedSymbol(null);
    setViewMode('sidebar_view');
    setReturnToPanFromTool(Boolean(options?.fromPan));
    if (options?.queryParams) {
      const next = new URLSearchParams(options.queryParams);
      next.set('tool', toolId);
      if (options.fromPan) next.set('from', 'pan');
      replaceCurrentUrlQuery(next, nextPath);
      return;
    }
    replaceCurrentUrlQuery(null, nextPath);
  };

  const goHome = useCallback(() => {
    shouldRestoreDashboardScrollRef.current = true;
    setArchiveToolState(null);
    setViewMode('dashboard');
    setSelectedSymbol(null);
    setCurrentTool(null);
    setReturnToPanFromTool(false);
    replaceCurrentUrlQuery(null, '/');
  }, []);

  useEffect(() => {
    if (!currentTool) return;
    if (isToolAllowedForHost(currentTool, runtimeHost)) return;
    goHome();
  }, [currentTool, goHome, runtimeHost]);

  const triggerCurrentToolBack = useCallback((): boolean => {
    if (!currentTool) return false;
    const marker = currentTool === 'pan'
      ? 'pan'
      : currentTool === 'dalurenPan'
        ? 'dalurenPan'
        : '';
    if (!marker) return false;
    const backButton = document.querySelector(`[data-qimen-internal-back="${marker}"]`) as HTMLButtonElement | null;
    if (!backButton) return false;
    backButton.click();
    return true;
  }, [currentTool]);

  const canHandleAppBack = useCallback((): boolean => (
    showActivateInput
    || showAuthModal
    || showProModal
    || Boolean(selectedSymbol)
    || (mobileTab === 'mine' && mineSubPage !== 'home')
    || Boolean(currentTool)
    || viewMode === 'sidebar_view'
    || mobileTab !== 'practice'
  ), [
    showActivateInput,
    showAuthModal,
    showProModal,
    selectedSymbol,
    mineSubPage,
    currentTool,
    viewMode,
    mobileTab,
  ]);

  const handleAppBack = useCallback(() => {
    if (showActivateInput) {
      setShowActivateInput(false);
      return;
    }
    if (showAuthModal) {
      setShowAuthModal(false);
      return;
    }
    if (showProModal) {
      setShowProModal(false);
      return;
    }
    if (selectedSymbol) {
      setSelectedSymbol(null);
      return;
    }
    if (mobileTab === 'mine' && mineSubPage !== 'home') {
      setMineSubPage('home');
      return;
    }
    if (currentTool && triggerCurrentToolBack()) {
      return;
    }
    if (currentTool) {
      if (returnToPanFromTool) {
        setCurrentTool('pan');
        setSelectedSymbol(null);
        setViewMode('sidebar_view');
        setReturnToPanFromTool(false);
        replaceCurrentUrlQuery(null, '/');
        return;
      }
      goHome();
      return;
    }
    if (viewMode === 'sidebar_view') {
      goHome();
      return;
    }
    if (mobileTab !== 'practice') {
      setMobileTab('practice');
    }
  }, [
    showActivateInput,
    showAuthModal,
    showProModal,
    selectedSymbol,
    mineSubPage,
    currentTool,
    returnToPanFromTool,
    viewMode,
    mobileTab,
    goHome,
    triggerCurrentToolBack,
  ]);

  const pushBackGuardState = useCallback(() => {
    if (disableHistoryBackGuardRef.current) return;
    const state = (window.history.state && typeof window.history.state === 'object')
      ? window.history.state as Record<string, unknown>
      : {};
    window.history.pushState({ ...state, __qimenBackHooked: true, __qimenBackLayer: Date.now() }, '');
  }, []);

  useEffect(() => {
    if (disableHistoryBackGuardRef.current || isDesktopViewport) return;
    const state = (window.history.state && typeof window.history.state === 'object')
      ? window.history.state as Record<string, unknown>
      : {};
    if (state.__qimenBackHooked === true) return;
    window.history.replaceState({ ...state, __qimenBackHooked: true, __qimenBackLayer: 0 }, '');
    window.history.pushState({ ...state, __qimenBackHooked: true, __qimenBackLayer: 1 }, '');
  }, [isDesktopViewport]);

  useEffect(() => {
    if (disableHistoryBackGuardRef.current || isDesktopViewport) return;
    const onPopState = () => {
      if (canHandleAppBack()) {
        handleAppBack();
      }
      pushBackGuardState();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [canHandleAppBack, handleAppBack, pushBackGuardState, isDesktopViewport]);

  useEffect(() => {
    if (isDesktopViewport) return;
    const onNativeBackButton = (event: Event) => {
      event.preventDefault();
      if (canHandleAppBack()) {
        handleAppBack();
      }
      pushBackGuardState();
    };
    document.addEventListener('backbutton', onNativeBackButton);
    return () => document.removeEventListener('backbutton', onNativeBackButton);
  }, [canHandleAppBack, handleAppBack, pushBackGuardState, isDesktopViewport]);

  const handleRootTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopViewport || viewMode === 'dashboard' || !canHandleAppBack()) return;
    if (event.touches.length !== 1) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('input, textarea, select, [contenteditable="true"], .no-edge-back')) return;
    const touch = event.touches[0];
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const edgeThreshold = 24;
    const edge: 'left' | 'right' | null = touch.clientX <= edgeThreshold
      ? 'left'
      : touch.clientX >= viewportWidth - edgeThreshold
        ? 'right'
        : null;
    edgeSwipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      edge,
      time: Date.now(),
    };
  };

  const handleRootTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopViewport || viewMode === 'dashboard' || !canHandleAppBack()) return;
    if (event.changedTouches.length !== 1) return;
    const start = edgeSwipeStartRef.current;
    edgeSwipeStartRef.current = { x: 0, y: 0, edge: null, time: 0 };
    if (!start.edge) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const elapsed = Date.now() - start.time;
    if (elapsed > 700) return;
    if (Math.abs(dx) < 42) return;
    if (Math.abs(dx) < Math.abs(dy) * 0.95) return;
    const directionOk = (start.edge === 'left' && dx > 0) || (start.edge === 'right' && dx < 0);
    if (!directionOk) return;
    event.preventDefault();
    handleAppBack();
  };

  const handleRootTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopViewport || viewMode === 'dashboard' || !canHandleAppBack()) return;
    if (event.touches.length !== 1) return;
    const start = edgeSwipeStartRef.current;
    if (!start.edge) return;
    const touch = event.touches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const directionOk = (start.edge === 'left' && dx > 0) || (start.edge === 'right' && dx < 0);
    if (!directionOk) return;
    if (Math.abs(dx) < 14) return;
    if (Math.abs(dx) < Math.abs(dy) * 0.9) return;
    event.preventDefault();
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      saveSidebarCollapsed(next);
      return next;
    });
  };

  const openPanTest = (options?: { preserveSession?: boolean }) => {
    if (viewMode === 'dashboard') {
      captureDashboardScrollPosition();
      shouldRestoreDashboardScrollRef.current = true;
    }
    scrollSidebarContentToTop();
    if (!options?.preserveSession) {
      clearPanSessionCache();
    }
    setCurrentTool('pan');
    trackNonAiToolUsage('pan');
    setSelectedSymbol(null);
    setViewMode('sidebar_view');
    setReturnToPanFromTool(false);
    replaceCurrentUrlQuery(null, '/');
  };

  const handleArchiveToolBack = useCallback(() => {
    shouldRestoreDashboardScrollRef.current = true;
    setArchiveToolState(null);
    setViewMode('dashboard');
    setSelectedSymbol(null);
    setCurrentTool(null);
    setReturnToPanFromTool(false);
    setMobileTab('mine');
    setMineSubPage('archive');
    replaceCurrentUrlQuery(null, '/');
  }, [replaceCurrentUrlQuery]);

  const handleArchiveRecordConsumed = useCallback((tool: ArchiveToolId, recordId: string) => {
    const targetId = String(recordId || '').trim();
    if (!targetId) return;
    setArchiveToolState((prev) => {
      if (!prev || prev.tool !== tool) return prev;
      if (prev.pendingRecordId !== targetId) return prev;
      return { ...prev, pendingRecordId: null };
    });
  }, []);

  const openArchiveRecord = useCallback((type: ArchiveRecordType, recordId: string) => {
    const targetId = String(recordId || '').trim();
    if (!targetId) return;
    const targetTool: ArchiveToolId = (
      type === 'qimen'
        ? 'pan'
        : type === 'daluren'
          ? 'dalurenPan'
          : 'sancaiLiuge'
    );
    if (targetTool === 'sancaiLiuge' && !memberActive) {
      openProModal('三才六格');
      return;
    }
    if (viewMode === 'dashboard') {
      captureDashboardScrollPosition();
      shouldRestoreDashboardScrollRef.current = true;
    }
    scrollSidebarContentToTop();
    setArchiveToolState({
      tool: targetTool,
      pendingRecordId: targetId,
    });
    setMobileTab('practice');
    setCurrentTool(targetTool);
    trackNonAiToolUsage(targetTool);
    setSelectedSymbol(null);
    setViewMode('sidebar_view');
    setReturnToPanFromTool(false);
    replaceCurrentUrlQuery(null, '/');
  }, [viewMode, memberActive, openProModal, captureDashboardScrollPosition, scrollSidebarContentToTop, replaceCurrentUrlQuery, trackNonAiToolUsage]);

  useEffect(() => {
    if (currentTool !== 'sancaiLiuge' || memberActive) return;
    openProModal('三才六格');
    setCurrentTool(null);
    replaceCurrentUrlQuery(null, '/');
  }, [currentTool, memberActive, openProModal, replaceCurrentUrlQuery]);

  const renderCurrentTool = () => (
    <AppToolRenderer
      currentTool={currentTool}
      userProfile={userProfile}
      fourXiangyiUnlocked={fourXiangyiUnlocked}
      effectiveMobileDockStyle={effectiveMobileDockStyle}
      memberActive={memberActive}
      theoryProAccess={theoryProAccess}
      originMemoryProAccess={originMemoryProAccess}
      originMemoryProCategoryUnlocks={originMemoryProCategoryUnlocks}
      returnToPanFromTool={returnToPanFromTool}
      onGoHome={goHome}
      onSetCurrentTool={setCurrentTool}
      onClearSelectedSymbol={() => setSelectedSymbol(null)}
      onSetViewMode={setViewMode}
      onSetMobileTab={setMobileTab}
      onSetReturnToPanFromTool={setReturnToPanFromTool}
      onReplaceCurrentUrlQuery={replaceCurrentUrlQuery}
      onEnterToolMode={(tool, options) => enterToolMode(tool as ToolType, options)}
      ensureTrialAvailable={(toolId, toolName) => ensureTrialAvailable(toolId as ToolType, toolName)}
      openProModal={openProModal}
      setShowAuthModal={setShowAuthModal}
      buildIntro={(id) => `${TOOL_HELP[id].desc}。${TOOL_HELP[id].usage}`}
      archiveToolState={archiveToolState}
      onArchiveBack={handleArchiveToolBack}
      onArchiveRecordConsumed={handleArchiveRecordConsumed}
      xiangyiCategoryMode={xiangyiCategoryMode}
    />
  );

  const renderActivationBlock = () => (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-left">
      <button
        onClick={() => setShowActivateInput(true)}
        className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-normal hover:bg-slate-800"
      >
        填写激活码
      </button>
      {showActivateInput && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 relative">
            <button onClick={() => setShowActivateInput(false)} className="absolute top-3 right-3 p-2 hover:bg-slate-100 rounded-full">
              <X className="w-4 h-4 text-slate-400" />
            </button>
            <h4 className="text-base font-semibold text-slate-900 mb-3">输入激活码</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                placeholder="请输入激活码"
                className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <button
                onClick={handleActivateSubmit}
                disabled={activationLoading}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-70"
              >
                {activationLoading ? '提交中...' : '立即激活'}
              </button>
            </div>
          </div>
        </div>
      )}
      <p className="text-sm text-slate-600 mt-3">
        请添加若轩老师微信
        <button onClick={copyWechat} className="inline-flex items-center gap-1 text-indigo-600 font-bold ml-1 hover:text-indigo-700">
          {WECHAT_ID}
          <Copy className="w-3 h-3" />
        </button>
        加入会员
      </p>
      {activationResult && (
        <div className={`mt-3 p-3 rounded-lg text-sm ${activationResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-rose-50 border border-rose-200 text-rose-700'}`}>
          {activationResult.msg}
        </div>
      )}
    </div>
  );

  const renderDisplaySettingsContent = () => (
    <div className="border-t border-slate-200 divide-y divide-slate-200">
      <div className="py-3 text-left">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">默认进入页面</p>
            <p className="text-xs text-slate-500">下次打开网页或 App 时生效</p>
          </div>
          <select
            value={defaultLaunchTarget}
            onChange={(e) => handleDefaultLaunchTargetChange(e.target.value as LaunchTarget)}
            className="max-w-[170px] rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
          >
            {launchTargetOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="py-3 text-left">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">排盘页设置</p>
            <p className="text-xs text-slate-500">底部“排盘”按钮打开的页面</p>
          </div>
          <select
            value={panDockTarget}
            onChange={(e) => handlePanDockTargetChange(e.target.value as PanDockTarget)}
            className="max-w-[170px] rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
          >
            {panDockTargetOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="py-3 text-left">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">默认排盘模式</p>
            <p className="text-xs text-slate-500">左普通，右专业</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDefaultPanModeChange('simple')}
              className={`text-xs ${defaultPanMode === 'simple' ? 'text-slate-900' : 'text-slate-500'}`}
            >
              普通
            </button>
            <button
              onClick={() => handleDefaultPanModeChange(defaultPanMode === 'simple' ? 'pro' : 'simple')}
              className={`relative h-6 w-11 rounded-full transition-colors ${defaultPanMode === 'pro' ? 'bg-[#8b6b3d]' : 'bg-slate-300'}`}
              aria-label="切换默认排盘模式"
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  defaultPanMode === 'pro' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <button
              onClick={() => handleDefaultPanModeChange('pro')}
              className={`text-xs ${defaultPanMode === 'pro' ? 'text-slate-900' : 'text-slate-500'}`}
            >
              专业
            </button>
          </div>
        </div>
      </div>

      <div className="py-3 text-left">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">应用显示列数</p>
            <p className="text-xs text-slate-500">一行3个或4个</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleMobileAppColsChange(3)}
              className={`text-xs ${mobileAppCols === 3 ? 'text-slate-900' : 'text-slate-500'}`}
            >
              3列
            </button>
            <button
              onClick={() => handleMobileAppColsChange(mobileAppCols === 3 ? 4 : 3)}
              className={`relative h-6 w-11 rounded-full transition-colors ${mobileAppCols === 4 ? 'bg-[#8b6b3d]' : 'bg-slate-300'}`}
              aria-label="切换应用列数"
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  mobileAppCols === 4 ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <button
              onClick={() => handleMobileAppColsChange(4)}
              className={`text-xs ${mobileAppCols === 4 ? 'text-slate-900' : 'text-slate-500'}`}
            >
              4列
            </button>
          </div>
        </div>
      </div>

      <div className="py-3 text-left">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">底部导航样式</p>
            <p className="text-xs text-slate-500">左默认，右 iOS（Pad 端固定默认）</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleMobileDockStyleChange('default')}
              className={`text-xs ${mobileDockStyle === 'default' ? 'text-slate-900' : 'text-slate-500'}`}
            >
              默认
            </button>
            <button
              onClick={() => handleMobileDockStyleChange(mobileDockStyle === 'default' ? 'ios' : 'default')}
              className={`relative h-6 w-11 rounded-full transition-colors ${mobileDockStyle === 'ios' ? 'bg-[#8b6b3d]' : 'bg-slate-300'}`}
              aria-label="切换底部导航样式"
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  mobileDockStyle === 'ios' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <button
              onClick={() => handleMobileDockStyleChange('ios')}
              className={`text-xs ${mobileDockStyle === 'ios' ? 'text-slate-900' : 'text-slate-500'}`}
            >
              iOS
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderQimenSettingsContent = () => (
    <div className="border-t border-slate-200 divide-y divide-slate-200">
      <div className="py-3 text-left">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-600">月干与主人公</p>
            <p className="text-xs text-slate-500">关闭（默认）：刻盘主人公=时干、事项=刻干；开启后：刻盘主人公改取日干（作月干）</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handleRemoteMonthAsSubjectChange(!remoteMonthAsSubject)}
              className={`relative h-6 w-11 rounded-full transition-colors ${remoteMonthAsSubject ? 'bg-[#8b6b3d]' : 'bg-slate-300'}`}
              aria-label="月干为主人公"
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  remoteMonthAsSubject ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      <div className="py-3 text-left">
        <div>
          <p className="text-sm font-medium text-slate-600">象意分类模式</p>
          <p className="text-xs text-slate-500 mt-0.5">保留二级：显示核心原理及人物/静物等子分类；简化：合并部分子分类，保留基础分类。</p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => {
                saveXiangyiCategoryMode('full');
                setXiangyiCategoryMode('full');
              }}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                xiangyiCategoryMode === 'full' ? 'border-[#8b6b3d] bg-amber-50 text-[#7b5a2f]' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              保留二级分类
            </button>
            <button
              type="button"
              onClick={() => {
                saveXiangyiCategoryMode('simple');
                setXiangyiCategoryMode('simple');
              }}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                xiangyiCategoryMode === 'simple' ? 'border-[#8b6b3d] bg-amber-50 text-[#7b5a2f]' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              简化分类
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMobileTabbarButtons = () => (
    <>
      <button
        onClick={() => {
          triggerNavTapEffect('pan');
          if (panDockTarget === 'pan') {
            openPanTest({ preserveSession: true });
            return;
          }
          void handleEnterOtherApp(panDockTarget as typeof OTHER_APPS[number]['id']);
        }}
        className={`mobile-tabbar-btn mobile-tabbar-btn-fx text-slate-400 hover:text-amber-700 ${activeNavTap === 'pan' ? 'is-tapping' : ''}`}
      >
        <Compass className="mobile-tabbar-icon" />
        <span className="mobile-tabbar-label">排盘</span>
      </button>
      <button
        onClick={() => {
          triggerNavTapEffect('practice');
          setMobileTab('practice');
        }}
        className={`mobile-tabbar-btn mobile-tabbar-btn-fx ${mobileTab === 'practice' ? 'text-amber-700' : 'text-slate-400'} ${activeNavTap === 'practice' ? 'is-tapping' : ''}`}
      >
        <LayoutGrid className="mobile-tabbar-icon" />
        <span className="mobile-tabbar-label">应用</span>
      </button>
      <button
        onClick={() => {
          triggerNavTapEffect('study');
          setMobileTab('study');
        }}
        className={`mobile-tabbar-btn mobile-tabbar-btn-fx ${mobileTab === 'study' ? 'text-amber-700' : 'text-slate-400'} ${activeNavTap === 'study' ? 'is-tapping' : ''}`}
      >
        <BookOpen className="mobile-tabbar-icon" />
        <span className="mobile-tabbar-label">学习</span>
      </button>
      <button
        onClick={() => {
          triggerNavTapEffect('mine');
          setMobileTab('mine');
        }}
        className={`mobile-tabbar-btn mobile-tabbar-btn-fx ${mobileTab === 'mine' ? 'text-amber-700' : 'text-slate-400'} ${activeNavTap === 'mine' ? 'is-tapping' : ''}`}
      >
        <User className="mobile-tabbar-icon" />
        <span className="mobile-tabbar-label">我的</span>
      </button>
    </>
  );

  const renderSettingsSections = () => (
    <UserSettingsSections
      isLoggedIn={isLoggedIn}
      onOpenAccount={() => openMineSubPage('account')}
      onOpenDisplay={() => openMineSubPage('display')}
      onOpenQimen={() => openMineSubPage('qimen')}
      onOpenNotice={() => openMineSubPage('notice')}
    />
  );

  const userInitial = String(userProfile?.name || '我').trim().charAt(0).toUpperCase();

  const renderUserPanel = (closeAfterLogout: boolean) => (
    <MinePanel
      mineSubPage={mineSubPage}
      setMineSubPage={setMineSubPage}
      userProfile={userProfile}
      userInitial={userInitial}
      memberActive={memberActive}
      membershipStatus={membershipStatus}
      currentPointsText={currentPointsText}
      expireTimeText={expireTimeText}
      featureRemainingSummaryText={featureRemainingSummaryText}
      showFeatureRemaining={showFeatureRemaining}
      featureRemainingError={featureRemainingError}
      featureRemainingLoading={featureRemainingLoading}
      featureRemainingSummary={featureRemainingSummary}
      accountNameDraft={accountNameDraft}
      setAccountNameDraft={setAccountNameDraft}
      accountEmailDraft={accountEmailDraft}
      setAccountEmailDraft={setAccountEmailDraft}
      accountPhoneDraft={accountPhoneDraft}
      setAccountPhoneDraft={setAccountPhoneDraft}
      accountSaveResult={accountSaveResult}
      onSaveAccountSettings={handleSaveAccountSettings}
      clientLogSubmitting={clientLogSubmitting}
      clientLogSubmitResult={clientLogSubmitResult}
      onSubmitClientLog={handleSubmitClientLog}
      onToggleFeatureRemaining={handleToggleFeatureRemaining}
      onOpenArchive={() => openMineSubPage('archive')}
      onOpenArchiveRecord={openArchiveRecord}
      onOpenCourse={() => {
        setMobileTab('study');
        setMineSubPage('home');
        setShowAuthModal(false);
      }}
      onOpenCertificate={() => window.alert('即将上线')}
      onOpenOrder={() => window.alert('即将上线')}
      onLogout={() => {
        handleLogout();
        if (closeAfterLogout) setShowAuthModal(false);
      }}
      renderSettingsSections={renderSettingsSections}
      renderDisplaySettingsContent={renderDisplaySettingsContent}
      renderQimenSettingsContent={renderQimenSettingsContent}
      renderActivationBlock={renderActivationBlock}
    />
  );

  const effectiveMobileDockStyle: MobileDockStyle = isPadViewport ? 'default' : mobileDockStyle;

  if (selectedSymbol) {
    const currentIndex = currentData.findIndex(item => item.id === selectedSymbol.id);
    const hasNext = currentIndex !== -1 && currentIndex < currentData.length - 1;
    const hasPrev = currentIndex > 0;

    const handleNextSymbol = () => {
      if (hasNext) setSelectedSymbol(currentData[currentIndex + 1]);
    };

    const handlePrevSymbol = () => {
      if (hasPrev) setSelectedSymbol(currentData[currentIndex - 1]);
    };

    return (
      <DetailView 
        data={selectedSymbol} 
        onBack={() => setSelectedSymbol(null)} 
        onNextSymbol={hasNext ? handleNextSymbol : undefined}
        onPrevSymbol={hasPrev ? handlePrevSymbol : undefined}
      />
    );
  }

  const renderDashboard = () => (
    <div className="max-w-[980px] mx-auto pb-24 lg:pb-0">
       <div className={`${isDesktopViewport ? 'flex' : 'hidden lg:flex'} items-center justify-between mb-8 p-6 bg-white rounded-2xl shadow-sm border border-slate-100`}>
       <div>
            <h1 className="text-3xl font-bold text-slate-900 font-serif mb-2">{APP_NAME}</h1>
          </div>
          <button 
             onClick={() => setShowAuthModal(true)}
             className="w-12 h-12 bg-slate-50 hover:bg-slate-100 rounded-full flex items-center justify-center text-slate-600 transition-colors border border-slate-200"
          >
             {isLoggedIn ? (
                 <span className="font-bold text-indigo-600">{userInitial}</span>
             ) : (
                 <Settings className="w-5 h-5" />
             )}
          </button>
       </div>

       <div className={`${mobileTab === 'practice' || isDesktopViewport ? 'flex' : 'hidden lg:flex'} flex-col gap-8`}>
          {isDesktopViewport && (
          <div className="order-3" style={{ order: sectionOrder.practice }}>
            <div className="flex items-center gap-2 mb-4 px-4 lg:px-0">
               <GraduationCap className="w-6 h-6 text-amber-600" />
               <h2 className="text-xl font-bold text-slate-800">{practiceCategoryLabel}</h2>
            </div>
            <div className={`grid gap-2 sm:gap-6 px-2 sm:px-4 lg:px-0 overflow-visible
              ${mobileAppCols === 4 ? 'grid-cols-4' : 'grid-cols-3'} ${mobileAppCols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:grid-cols-4 xl:grid-cols-4`}>
                  {/* Desktop Card - 奇门排盘 (Pad 及以上采用电脑端横版，文字在卡片内) */}
                  <button
                    onClick={() => openPanTest({ preserveSession: false })}
                    className="hidden"
                  >
                    <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                        <Compass className="icon-app-xl text-white" />
                    </div>
                    <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                        <Compass className="icon-app-sm text-amber-100" />
                    </div>
                    <div className="relative z-10 w-full">
                        <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">奇门排盘</h3>
                    </div>
                  </button>

                  {/* Mobile Card - 奇门排盘 (仅手机窄屏) */}
                  <button
                    onClick={() => openPanTest({ preserveSession: false })}
                    className={`hidden
                      ${mobileAppCols === 4 
                        ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible' 
                        : 'p-1.5 aspect-square text-white border-2 bg-gradient-to-br from-amber-600 to-orange-700 border-amber-300/40 overflow-hidden'
                      }`}
                  >
                    {mobileAppCols === 4 ? (
                       <>
                          <div className="w-full aspect-square rounded-2xl bg-gradient-to-br from-amber-600 to-orange-700 flex items-center justify-center shadow-sm overflow-hidden shrink-0">
                             <Compass className="icon-app-mobile-4col text-white/90" />
                          </div>
                          <h3 className="mobile-app-label-4col">奇门排盘</h3>
                       </>
                    ) : (
                       <div className="mobile-app-card-inner">
                          <Compass className="icon-app-mobile-3col text-white/90" />
                          <span className="mobile-app-label-3col text-white">奇门排盘</span>
                       </div>
                    )}
                  </button>

                  {orderedPracticeTools.map((tool) => (
                      <React.Fragment key={tool.id}>
                          {/* Desktop Card (Pad 及以上采用电脑端横版) */}
                          <button
                              onClick={() => handleEnterProTool(tool.id)}
                              className={`hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border border-slate-200/20
                                ${tool.id === 'trainer' ? 'bg-gradient-to-br from-indigo-600 to-indigo-800' :
                                  tool.id === 'decoder' ? 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-900' :
                                  tool.id === 'findObject' ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
                                  'bg-gradient-to-br from-rose-600 to-rose-800'}`}
                          >
                              <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                                  <tool.icon className="icon-app-xl text-white" />
                              </div>
                              <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                                  <tool.icon className={`icon-app-sm ${tool.color}`} />
                              </div>
                              <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(tool.label); }} />
                              <div className="relative z-10 w-full">
                                  <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">{tool.label}</h3>
                              </div>
                          </button>

                          {/* Mobile Card (仅手机窄屏) */}
                          <button
                              onClick={() => handleEnterProTool(tool.id)}
                              className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center justify-center text-center
                                  ${mobileAppCols === 4 
                                    ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible' 
                                    : `p-1.5 aspect-square text-white border-2 overflow-hidden 
                                        ${tool.id === 'trainer' ? 'bg-gradient-to-br from-indigo-600 to-indigo-800 border-indigo-400/30' :
                                          tool.id === 'decoder' ? 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-900 border-fuchsia-400/30' :
                                          tool.id === 'findObject' ? 'bg-gradient-to-br from-blue-600 to-blue-800 border-blue-400/30' :
                                          'bg-gradient-to-br from-rose-600 to-rose-800 border-rose-400/30'}`}`}
                          >
                              {mobileAppCols === 4 ? (
                                 <>
                                    <div className={`relative w-full aspect-square rounded-2xl flex items-center justify-center shadow-sm overflow-hidden shrink-0
                                      ${tool.id === 'trainer' ? 'bg-gradient-to-br from-indigo-600 to-indigo-800' :
                                        tool.id === 'decoder' ? 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-900' :
                                        tool.id === 'findObject' ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
                                        'bg-gradient-to-br from-rose-600 to-rose-800'}`}>
                                       <ProCorner
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openProModal(tool.label);
                                          }}
                                        />
                                       <tool.icon className="icon-app-mobile-4col text-white/90" />
                                    </div>
                                    <h3 className="mobile-app-label-4col">{tool.label}</h3>
                                 </>
                              ) : (
                                 <div className="mobile-app-card-inner relative">
                                    <ProCorner
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openProModal(tool.label);
                                      }}
                                    />
                                    <tool.icon className={`icon-app-mobile-3col ${tool.color}`} />
                                    <span className="mobile-app-label-3col text-white">{tool.label}</span>
                                 </div>
                              )}
                          </button>
                      </React.Fragment>
                  ))}
            </div>
          </div>
          )}

          <div className="order-1" style={{ order: sectionOrder.other }}>
            <div className="flex items-center gap-2 mb-4 px-4 lg:px-0">
              <LayoutGrid className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-bold text-slate-800">{otherCategoryLabel}</h2>
            </div>
            <div className={`grid gap-2 sm:gap-6 px-2 sm:px-4 lg:px-0 overflow-visible
              ${mobileAppCols === 4 ? 'grid-cols-4' : 'grid-cols-3'} ${mobileAppCols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:grid-cols-4 xl:grid-cols-4`}>
              {/* Desktop Card - 奇门排盘 (Pad 及以上采用电脑端横版，文字在卡片内) */}
              <button
                onClick={() => openPanTest({ preserveSession: false })}
                className="hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border border-slate-200/20 bg-gradient-to-br from-amber-600 to-orange-700"
              >
                <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                    <Compass className="icon-app-xl text-white" />
                </div>
                <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                    <Compass className="icon-app-sm text-amber-100" />
                </div>
                <div className="relative z-10 w-full">
                    <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">奇门排盘</h3>
                </div>
              </button>
              {/* Mobile Card - 奇门排盘 (仅手机窄屏) */}
              <button
                onClick={() => openPanTest({ preserveSession: false })}
                className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center justify-center text-center
                  ${mobileAppCols === 4 
                    ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible' 
                    : 'p-1.5 aspect-square text-white border-2 bg-gradient-to-br from-amber-600 to-orange-700 border-amber-300/40 overflow-hidden'
                  }`}
              >
                {mobileAppCols === 4 ? (
                   <>
                      <div className="w-full aspect-square rounded-2xl bg-gradient-to-br from-amber-600 to-orange-700 flex items-center justify-center shadow-sm overflow-hidden shrink-0">
                         <Compass className="icon-app-mobile-4col text-white/90" />
                      </div>
                      <h3 className="mobile-app-label-4col">奇门排盘</h3>
                   </>
                ) : (
                   <div className="mobile-app-card-inner">
                      <Compass className="icon-app-mobile-3col text-white/90" />
                      <span className="mobile-app-label-3col text-white">奇门排盘</span>
                   </div>
                )}
              </button>
              {orderedOtherApps.map((app) => {
                const cornerLabel = getOtherAppCornerLabel(app.id);
                return (
                  <React.Fragment key={app.id}>
                    {/* Desktop Card (Pad 及以上采用电脑端横版) */}
                    <button
                      onClick={() => handleEnterOtherApp(app.id)}
                      className={`hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border border-slate-200/20
                        ${getOtherAppGradient(app.id)}`}
                    >
                      <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                          <app.icon className="icon-app-xl text-white" />
                      </div>
                      <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                          <app.icon className="icon-app-sm text-white/90" />
                      </div>
                      {cornerLabel && (
                        <ProCorner
                          label={cornerLabel}
                          onClick={(e) => {
                            e.stopPropagation();
                            openProModal(app.label);
                          }}
                        />
                      )}
                      <div className="relative z-10 w-full">
                          <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">{app.label}</h3>
                      </div>
                    </button>

                    {/* Mobile Card (仅手机窄屏) */}
                    <button
                      onClick={() => handleEnterOtherApp(app.id)}
                      className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center justify-center text-center
                        ${mobileAppCols === 4 
                          ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible' 
                          : `p-1.5 aspect-square text-white border-2 overflow-hidden 
                              ${getOtherAppGradient(app.id)} ${getOtherAppBorder(app.id)}`}`}
                    >
                      {mobileAppCols === 4 ? (
                        <>
                          <div className={`relative w-full aspect-square rounded-2xl flex items-center justify-center shadow-sm overflow-hidden shrink-0
                            ${getOtherAppGradient(app.id)}`}>
                            {cornerLabel && (
                              <ProCorner
                                label={cornerLabel}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openProModal(app.label);
                                }}
                              />
                            )}
                            <app.icon className="icon-app-mobile-4col text-white/90" />
                          </div>
                          <h3 className="mobile-app-label-4col">{app.label}</h3>
                        </>
                      ) : (
                        <div className="mobile-app-card-inner relative">
                          {cornerLabel && (
                            <ProCorner
                              label={cornerLabel}
                              onClick={(e) => {
                                e.stopPropagation();
                                openProModal(app.label);
                              }}
                            />
                          )}
                          <app.icon className={`icon-app-mobile-3col ${app.color}`} />
                          <span className="mobile-app-label-3col text-white">{app.label}</span>
                        </div>
                      )}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {orderedPsychApps.length > 0 && (
          <div className="order-2" style={{ order: sectionOrder.psych }}>
            <div className="flex items-center gap-2 mb-4 px-4 lg:px-0">
              <Brain className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-slate-800">{psychCategoryLabel}</h2>
            </div>
            <div className={`grid gap-2 sm:gap-6 px-2 sm:px-4 lg:px-0 overflow-visible ${mobileAppCols === 4 ? 'grid-cols-4' : 'grid-cols-3'} ${mobileAppCols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:grid-cols-4 xl:grid-cols-4`}>
              {orderedPsychApps.map((app) => (
                <React.Fragment key={app.id}>
                  <button
                    onClick={() => enterToolMode(app.id)}
                    className={`hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border-2 bg-gradient-to-br ${app.gradient} ${app.borderColor}`}
                  >
                    <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                      <app.icon className="icon-app-xl text-white" />
                    </div>
                    <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                      <app.icon className="icon-app-sm text-white/90" />
                    </div>
                    <div className="relative z-10 w-full">
                      <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">{app.label}</h3>
                    </div>
                  </button>
                  <button
                    onClick={() => enterToolMode(app.id)}
                    className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center text-center
                      ${mobileAppCols === 4 
                        ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible' 
                        : `p-1.5 aspect-square text-white border-2 overflow-hidden bg-gradient-to-br ${app.gradient} ${app.borderColor}`}`}
                  >
                    {mobileAppCols === 4 ? (
                      <>
                        <div className={`w-full aspect-square rounded-2xl flex items-center justify-center overflow-hidden shrink-0 bg-gradient-to-br ${app.gradient}`}>
                          <app.icon className="icon-app-mobile-4col text-white/90" />
                        </div>
                        <h3 className="mobile-app-label-4col">{app.label}</h3>
                      </>
                    ) : (
                      <div className="mobile-app-card-inner">
                        <app.icon className="icon-app-mobile-3col text-white/90" />
                        <span className="mobile-app-label-3col text-white">{app.label}</span>
                      </div>
                    )}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
          )}

          {isDesktopViewport && (
            <div className="order-4" style={{ order: sectionOrder.study }}>
              <div className="flex items-center gap-2 mb-4 px-4 lg:px-0">
                 <BookOpen className="w-6 h-6 text-indigo-600" />
                 <h2 className="text-xl font-bold text-slate-800">{studyCategoryLabel}</h2>
              </div>
              <div className={`grid gap-2 sm:gap-6 px-2 sm:px-4 lg:px-0 overflow-visible
                ${mobileAppCols === 4 ? 'grid-cols-4' : 'grid-cols-3'} ${mobileAppCols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:grid-cols-4 xl:grid-cols-4`}>
                {orderedStudyCardIds.map((cardId) => {
                  const cardLabel = cardId === 'theory'
                    ? '基础知识'
                    : cardId === 'originMemory'
                      ? '象意速记'
                      : '玄学故事';
                  const Icon = cardId === 'theory'
                    ? Library
                    : cardId === 'originMemory'
                      ? Zap
                      : BookOpen;
                  const click = () => {
                    if (cardId === 'theory') {
                      enterToolMode('theory');
                      return;
                    }
                    if (cardId === 'originMemory') {
                      enterToolMode('originMemory');
                      return;
                    }
                    enterToolMode('xuanxueStories');
                  };
                  const desktopBg = cardId === 'theory'
                    ? 'bg-gradient-to-br from-emerald-600 to-emerald-800'
                    : cardId === 'originMemory'
                      ? 'bg-gradient-to-br from-indigo-600 to-violet-800'
                      : 'bg-gradient-to-br from-amber-700 to-orange-900';
                  const mobileBorder = cardId === 'theory'
                    ? 'border-emerald-300/35'
                    : cardId === 'originMemory'
                      ? 'border-indigo-300/35'
                      : 'border-amber-300/35';
                  return (
                    <React.Fragment key={`desktop-${cardId}`}>
                      <button
                        onClick={click}
                        className={`hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border border-slate-200/20 ${desktopBg}`}
                      >
                        <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                          <Icon className="icon-app-xl text-white" />
                        </div>
                        <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                          <Icon className="icon-app-sm text-white/90" />
                        </div>
                        <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(cardLabel); }} />
                        <div className="relative z-10 w-full">
                          <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">{cardLabel}</h3>
                        </div>
                      </button>
                      <button
                        onClick={click}
                        className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center justify-center text-center
                          ${mobileAppCols === 4
                            ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible'
                            : `p-1.5 aspect-square text-white border-2 overflow-hidden ${desktopBg} ${mobileBorder}`}`}
                      >
                        {mobileAppCols === 4 ? (
                          <>
                            <div className={`relative w-full aspect-square rounded-2xl flex items-center justify-center shadow-sm overflow-hidden shrink-0 ${desktopBg}`}>
                              <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(cardLabel); }} />
                              <Icon className="icon-app-mobile-4col text-white/90" />
                            </div>
                            <h3 className="mobile-app-label-4col">
                              {cardLabel}
                            </h3>
                          </>
                        ) : (
                          <div className="mobile-app-card-inner relative">
                            <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(cardLabel); }} />
                            <Icon className="icon-app-mobile-3col text-white/90" />
                            <span className="mobile-app-label-3col text-white">{cardLabel}</span>
                          </div>
                        )}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
       </div>

       <div className={`space-y-6 ${!isDesktopViewport && mobileTab === 'study' ? 'block' : 'hidden'}`}>
          <div>
            <div className="flex items-center gap-2 mb-4 px-4 lg:px-0">
              <GraduationCap className="w-6 h-6 text-amber-600" />
              <h2 className="text-xl font-bold text-slate-800">{practiceCategoryLabel}</h2>
            </div>
            <div className={`grid gap-2 sm:gap-6 px-2 sm:px-4 lg:px-0 overflow-visible
              ${mobileAppCols === 4 ? 'grid-cols-4' : 'grid-cols-3'} ${mobileAppCols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:grid-cols-4 xl:grid-cols-4`}>
              {orderedPracticeTools.map((tool) => (
                <React.Fragment key={`study-tab-${tool.id}`}>
                  <button
                    onClick={() => handleEnterProTool(tool.id)}
                    className={`hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border border-slate-200/20
                      ${tool.id === 'trainer' ? 'bg-gradient-to-br from-indigo-600 to-indigo-800' :
                        tool.id === 'decoder' ? 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-900' :
                        tool.id === 'findObject' ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
                        'bg-gradient-to-br from-rose-600 to-rose-800'}`}
                  >
                    <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                      <tool.icon className="icon-app-xl text-white" />
                    </div>
                    <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                      <tool.icon className={`icon-app-sm ${tool.color}`} />
                    </div>
                    <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(tool.label); }} />
                    <div className="relative z-10 w-full">
                      <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">{tool.label}</h3>
                    </div>
                  </button>

                  <button
                    onClick={() => handleEnterProTool(tool.id)}
                    className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center justify-center text-center
                      ${mobileAppCols === 4
                        ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible'
                        : `p-1.5 aspect-square text-white border-2 overflow-hidden 
                            ${tool.id === 'trainer' ? 'bg-gradient-to-br from-indigo-600 to-indigo-800 border-indigo-400/30' :
                              tool.id === 'decoder' ? 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-900 border-fuchsia-400/30' :
                              tool.id === 'findObject' ? 'bg-gradient-to-br from-blue-600 to-blue-800 border-blue-400/30' :
                              'bg-gradient-to-br from-rose-600 to-rose-800 border-rose-400/30'}`}`}
                  >
                    {mobileAppCols === 4 ? (
                      <>
                        <div className={`relative w-full aspect-square rounded-2xl flex items-center justify-center shadow-sm overflow-hidden shrink-0
                          ${tool.id === 'trainer' ? 'bg-gradient-to-br from-indigo-600 to-indigo-800' :
                            tool.id === 'decoder' ? 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-900' :
                            tool.id === 'findObject' ? 'bg-gradient-to-br from-blue-600 to-blue-800' :
                            'bg-gradient-to-br from-rose-600 to-rose-800'}`}>
                          <ProCorner
                            onClick={(e) => {
                              e.stopPropagation();
                              openProModal(tool.label);
                            }}
                          />
                          <tool.icon className="icon-app-mobile-4col text-white/90" />
                        </div>
                        <h3 className="mobile-app-label-4col">{tool.label}</h3>
                      </>
                    ) : (
                      <div className="mobile-app-card-inner relative">
                        <ProCorner
                          onClick={(e) => {
                            e.stopPropagation();
                            openProModal(tool.label);
                          }}
                        />
                        <tool.icon className={`icon-app-mobile-3col ${tool.color}`} />
                        <span className="mobile-app-label-3col text-white">{tool.label}</span>
                      </div>
                    )}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4 px-4 lg:px-0">
             <BookOpen className="w-6 h-6 text-indigo-600" />
             <h2 className="text-xl font-bold text-slate-800">{studyCategoryLabel}</h2>
          </div>
          <div className={`grid gap-2 sm:gap-6 px-2 sm:px-4 lg:px-0 overflow-visible
            ${mobileAppCols === 4 ? 'grid-cols-4' : 'grid-cols-3'} ${mobileAppCols === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} lg:grid-cols-4 xl:grid-cols-4`}>
            {orderedStudyCardIds.map((cardId) => {
              const cardLabel = cardId === 'theory'
                ? '基础知识'
                : cardId === 'originMemory'
                  ? '象意速记'
                  : '玄学故事';
              const Icon = cardId === 'theory'
                ? Library
                : cardId === 'originMemory'
                  ? Zap
                  : BookOpen;
              const click = () => {
                if (cardId === 'theory') {
                  enterToolMode('theory');
                  return;
                }
                if (cardId === 'originMemory') {
                  enterToolMode('originMemory');
                  return;
                }
                enterToolMode('xuanxueStories');
              };
              const desktopBg = cardId === 'theory'
                ? 'bg-gradient-to-br from-emerald-600 to-emerald-800'
                : cardId === 'originMemory'
                  ? 'bg-gradient-to-br from-indigo-600 to-violet-800'
                  : 'bg-gradient-to-br from-amber-700 to-orange-900';
              const mobileBorder = cardId === 'theory'
                ? 'border-emerald-300/35'
                : cardId === 'originMemory'
                  ? 'border-indigo-300/35'
                  : 'border-amber-300/35';
              return (
                <React.Fragment key={cardId}>
                  <button
                    onClick={click}
                    className={`hidden sm:flex group relative flex-col p-3 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left aspect-[1.65/1] min-h-[5.35rem] justify-between overflow-hidden text-white border border-slate-200/20 ${desktopBg}`}
                  >
                    <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 md:-bottom-1 md:-right-1 opacity-25 md:opacity-35 group-hover:opacity-40 md:group-hover:opacity-50 transition-opacity transform scale-100 sm:scale-[1.05] group-hover:rotate-12 duration-500 origin-bottom-right">
                      <Icon className="icon-app-xl text-white" />
                    </div>
                    <div className="icon-box-md rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-1.5 group-hover:scale-110 transition-transform shrink-0">
                      <Icon className="icon-app-sm text-white/90" />
                    </div>
                    <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(cardLabel); }} />
                    <div className="relative z-10 w-full">
                      <h3 className="text-base md:text-lg font-bold text-white leading-tight whitespace-nowrap text-left">{cardLabel}</h3>
                    </div>
                  </button>
                  <button
                    onClick={click}
                    className={`sm:hidden group relative flex flex-col rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 items-center justify-center text-center
                      ${mobileAppCols === 4
                        ? 'mobile-app-card-4col p-0 bg-transparent aspect-auto overflow-visible'
                        : `p-1.5 aspect-square text-white border-2 overflow-hidden ${desktopBg} ${mobileBorder}`}`}
                  >
                    {mobileAppCols === 4 ? (
                      <>
                        <div className={`relative w-full aspect-square rounded-2xl flex items-center justify-center shadow-sm overflow-hidden shrink-0 ${desktopBg}`}>
                          <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(cardLabel); }} />
                          <Icon className="icon-app-mobile-4col text-white/90" />
                        </div>
                        <h3 className="mobile-app-label-4col">
                          {cardLabel}
                        </h3>
                      </>
                    ) : (
                      <div className="mobile-app-card-inner relative">
                        <ProCorner onClick={(e) => { e.stopPropagation(); openProModal(cardLabel); }} />
                        <Icon className="icon-app-mobile-3col text-white/90" />
                        <span className="mobile-app-label-3col text-white">{cardLabel}</span>
                      </div>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
       </div>

       {!isDesktopViewport && mobileTab === 'mine' && (
           <div className={mineSubPage === 'home' ? 'py-8' : 'pt-0 pb-8'}>
              {!isLoggedIn ? (
                  <div className="w-full max-w-2xl mx-auto px-1 sm:px-2 space-y-3">
                    <Auth onLogin={handleLoginSuccess} />
                    <div className="rounded-3xl bg-white p-4 shadow-sm border border-slate-100 -mx-1 sm:mx-0">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">设置</h3>
                      {renderSettingsSections()}
                    </div>
                  </div>
              ) : (
                  <div className={`w-full max-w-2xl mx-auto ${mineSubPage === 'archive' ? 'px-0 sm:px-0' : 'px-1 sm:px-2'}`}>
                    {renderUserPanel(false)}
                  </div>
              )}
           </div>
       )}

    </div>
  );

  const renderSidebarView = () => (
    <div className={`flex ${isDesktopViewport ? 'h-full' : 'flex-1 h-screen'} overflow-hidden bg-slate-50`}>
        <div className={`${isDesktopViewport ? 'flex' : 'hidden'} h-full min-h-0 bg-slate-900 text-slate-300 flex-col shrink-0 border-r border-slate-800 z-20 transition-[width] duration-200 ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
            <div className={`border-b border-slate-800 shrink-0 ${sidebarCollapsed ? 'px-2 py-2' : 'px-3 py-3'}`}>
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} gap-2`}>
                <button
                  onClick={goHome}
                  title="返回主页"
                  className={`flex items-center text-amber-400 hover:bg-slate-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center w-10 h-10' : 'gap-3 px-3 py-2 flex-1'}`}
                >
                  <Home className="w-6 h-6 shrink-0" />
                  {!sidebarCollapsed && <span className="font-bold">返回主页</span>}
                </button>
                {!sidebarCollapsed && (
                  <button
                    onClick={toggleSidebarCollapsed}
                    title="收起功能栏"
                    className="w-9 h-9 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors flex items-center justify-center"
                  >
                    <ChevronRight className="w-5 h-5 rotate-180" />
                  </button>
                )}
              </div>
              {sidebarCollapsed && (
                <div className="mt-2 flex justify-center">
                  <button
                    onClick={toggleSidebarCollapsed}
                    title="展开功能栏"
                    className="w-9 h-9 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors flex items-center justify-center"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
            <nav className={`sidebar-scroll-hidden flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col ${sidebarCollapsed ? 'py-3 gap-4' : 'py-4 gap-6'}`}>
                <div className="order-3 space-y-1" style={{ order: sectionOrder.practice }}>
                    {!sidebarCollapsed && (
                      <button
                        onClick={() => toggleSidebarSection('practice')}
                        className="w-full flex items-center gap-2 px-4 py-2 mb-1 -mx-1 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-slate-300 transition-colors group"
                      >
                        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${sidebarSectionsExpanded.practice ? '' : '-rotate-90'}`} />
                        <span className="text-sm font-medium text-slate-400 group-hover:text-slate-300">{practiceCategoryLabel}</span>
                      </button>
                    )}
                    {(!sidebarCollapsed ? sidebarSectionsExpanded.practice : true) && (
                    <>
                    {orderedPracticeTools.map(tool => (
                        <button
                          key={tool.id}
                          onClick={() => handleEnterProTool(tool.id)}
                          title={tool.label}
                          className={`w-full flex items-center py-3 transition-all ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} ${currentTool === tool.id ? 'bg-amber-600 text-white border-r-4 border-white' : 'hover:bg-slate-800 hover:text-white'}`}
                        >
                            <tool.icon className="w-5 h-5 shrink-0" />
                            {!sidebarCollapsed && <span className="text-sm font-medium">{tool.label}</span>}
                        </button>
                    ))}
                    </>
                    )}
                </div>
                <div className="order-1 space-y-1" style={{ order: sectionOrder.other }}>
                    {!sidebarCollapsed && (
                      <button
                        onClick={() => toggleSidebarSection('other')}
                        className="w-full flex items-center gap-2 px-4 py-2 mb-1 -mx-1 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-slate-300 transition-colors group"
                      >
                        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${sidebarSectionsExpanded.other ? '' : '-rotate-90'}`} />
                        <span className="text-sm font-medium text-slate-400 group-hover:text-slate-300">{otherCategoryLabel}</span>
                      </button>
                    )}
                    {(!sidebarCollapsed ? sidebarSectionsExpanded.other : true) && (
                    <>
                      <button
                        onClick={() => openPanTest({ preserveSession: false })}
                        title="奇门排盘"
                        className={`w-full flex items-center py-3 transition-all ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} ${currentTool === 'pan' ? 'bg-amber-600 text-white border-r-4 border-white' : 'hover:bg-slate-800 hover:text-white'}`}
                      >
                        <Compass className="w-5 h-5 shrink-0" />
                        {!sidebarCollapsed && <span className="text-sm font-medium">奇门排盘</span>}
                      </button>
                      {orderedOtherApps.map(app => (
                          <button
                            key={app.id}
                            onClick={() => handleEnterOtherApp(app.id)}
                            title={app.label}
                            className={`w-full flex items-center py-3 transition-all ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} ${currentTool === app.id ? 'bg-sky-600 text-white border-r-4 border-white' : 'hover:bg-slate-800 hover:text-white'}`}
                          >
                              <app.icon className="w-5 h-5 shrink-0" />
                              {!sidebarCollapsed && <span className="text-sm font-medium">{app.label}</span>}
                          </button>
                      ))}
                    </>)}
                </div>
                {orderedPsychApps.length > 0 && (
                <div className="order-2 space-y-1" style={{ order: sectionOrder.psych }}>
                    {!sidebarCollapsed && (
                      <button
                        onClick={() => toggleSidebarSection('psych')}
                        className="w-full flex items-center gap-2 px-4 py-2 mb-1 -mx-1 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-slate-300 transition-colors group"
                      >
                        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${sidebarSectionsExpanded.psych ? '' : '-rotate-90'}`} />
                        <span className="text-sm font-medium text-slate-400 group-hover:text-slate-300">{psychCategoryLabel}</span>
                      </button>
                    )}
                    {(!sidebarCollapsed ? sidebarSectionsExpanded.psych : true) && (
                    orderedPsychApps.map(app => (
                        <button
                          key={app.id}
                          onClick={() => enterToolMode(app.id)}
                          title={app.label}
                          className={`w-full flex items-center py-3 transition-all ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} ${currentTool === app.id ? 'bg-blue-600 text-white border-r-4 border-white' : 'hover:bg-slate-800 hover:text-white'}`}
                        >
                            <app.icon className="w-5 h-5 shrink-0" />
                            {!sidebarCollapsed && <span className="text-sm font-medium">{app.label}</span>}
                        </button>
                    )))}
                </div>
                )}
                <div className="order-4 space-y-1" style={{ order: sectionOrder.study }}>
                    {!sidebarCollapsed && (
                      <button
                        onClick={() => toggleSidebarSection('learn')}
                        className="w-full flex items-center gap-2 px-4 py-2 mb-1 -mx-1 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-slate-300 transition-colors group"
                      >
                        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${sidebarSectionsExpanded.learn ? '' : '-rotate-90'}`} />
                        <span className="text-sm font-medium text-slate-400 group-hover:text-slate-300">{studyCategoryLabel}</span>
                      </button>
                    )}
                    {(!sidebarCollapsed ? sidebarSectionsExpanded.learn : true) && (
                    <>
                      {orderedStudyCardIds.map((cardId) => {
                        const cardLabel = cardId === 'theory'
                          ? '基础知识'
                          : cardId === 'originMemory'
                            ? '象意速记'
                            : '玄学故事';
                        const Icon = cardId === 'theory'
                          ? Library
                          : cardId === 'originMemory'
                            ? Zap
                            : BookOpen;
                        const active = currentTool === cardId;
                        const click = () => {
                          if (cardId === 'theory') {
                            enterToolMode('theory');
                            return;
                          }
                          if (cardId === 'originMemory') {
                            enterToolMode('originMemory');
                            return;
                          }
                          enterToolMode('xuanxueStories');
                        };
                        const activeClass = cardId === 'theory'
                          ? 'bg-emerald-600 text-white border-r-4 border-white'
                          : cardId === 'originMemory'
                            ? 'bg-indigo-600 text-white border-r-4 border-white'
                            : 'bg-amber-600 text-white border-r-4 border-white';
                        return (
                          <button
                            key={cardId}
                            onClick={click}
                            title={cardLabel}
                            className={`w-full flex items-center py-3 transition-all ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} ${active ? activeClass : 'hover:bg-slate-800 hover:text-white'}`}
                          >
                            <Icon className="w-5 h-5 shrink-0" />
                            {!sidebarCollapsed && <span className="text-sm font-medium">{cardLabel}</span>}
                          </button>
                        );
                      })}
                    </>
                    )}
                </div>
            </nav>
            <div className={`shrink-0 border-t border-slate-800 ${sidebarCollapsed ? 'pt-3 pb-3' : 'pt-2 pb-2'}`}>
              <button
                onClick={() => setShowAuthModal(true)}
                title="我的"
                className={`w-full flex items-center py-3 transition-all hover:bg-slate-800 hover:text-white ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-4'}`}
              >
                <User className="w-5 h-5 shrink-0" />
                {!sidebarCollapsed && <span className="text-sm font-medium">我的</span>}
              </button>
            </div>
        </div>

        <div
          ref={sidebarContentScrollRef}
          className={`flex-1 h-full overflow-y-auto bg-slate-50 relative ${currentTool ? 'overscroll-x-none touch-pan-y' : ''}`}
          style={currentTool ? { overscrollBehaviorX: 'none', touchAction: 'pan-y' } : undefined}>
             {currentTool ? renderCurrentTool() : (
                 <div className="p-4 lg:p-8">
                    <div className="max-w-5xl mx-auto pb-20 lg:pb-0">
                        {!isDesktopViewport && (
                        <div className="flex items-center mb-6">
                            <button onClick={goHome} className="p-2 -ml-2 mr-2 hover:bg-slate-200 rounded-full transition-colors">
                                <ArrowLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <h2 className="text-xl font-bold text-slate-900">{CATEGORIES.find(c => c.id === activeCategory)?.label}</h2>
                        </div>
                        )}
                        {isDesktopViewport && (
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">{CATEGORIES.find(c => c.id === activeCategory)?.label}</h2>
                                <p className="text-slate-500 text-sm mt-1">选择符号查看核心原点与推演逻辑</p>
                            </div>
                        </div>
                        )}
                        <div className="relative mb-6">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input type="text" placeholder="搜索本类符号..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {currentData.map((item) => (
                                <button key={item.id} onClick={() => setSelectedSymbol(item)} className="group flex flex-col items-start p-2.5 sm:p-3.5 lg:p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all text-left relative overflow-hidden">
                                    <div className="flex items-center justify-between w-full mb-1.5 lg:mb-2">
                                        <div className="w-7 h-7 lg:w-9 lg:h-9 rounded-lg bg-slate-900 text-amber-50 flex items-center justify-center font-serif text-base lg:text-lg shadow-md group-hover:scale-110 transition-transform z-10">{item.char.charAt(0)}</div>
                                        <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <h3 className="text-sm lg:text-base font-bold text-slate-900 mb-0.5 z-10">{item.pinyin}</h3>
                                    <p className="text-xs text-slate-500 line-clamp-2 z-10">{item.summary}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                 </div>
             )}
        </div>
    </div>
  );

  const isMobileArchivePanel = !isDesktopViewport && viewMode === 'dashboard' && mobileTab === 'mine' && mineSubPage === 'archive';

  return (
    <div
      className={`min-h-screen bg-slate-50 text-slate-800 font-sans ${isDesktopViewport ? 'desktop-mode h-screen overflow-hidden' : 'flex flex-col h-screen overflow-hidden'}`}
      onTouchStart={handleRootTouchStart}
      onTouchMove={handleRootTouchMove}
      onTouchEnd={handleRootTouchEnd}
      style={{ overscrollBehaviorX: 'none', touchAction: 'pan-y' }}
    >
      {viewMode === 'dashboard' && !isDesktopViewport && !isMobileArchivePanel && (
          <div
            className="lg:hidden bg-transparent text-slate-900 px-4 pb-3 flex items-center justify-between shrink-0 z-20"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
          >
            <h1 className="text-lg font-bold font-serif tracking-wider">{APP_NAME}</h1>
            <ShieldCheck className="w-5 h-5 text-slate-500" />
          </div>
      )}
      <div
        className={isDesktopViewport ? 'h-full overflow-hidden' : 'flex-1 overflow-hidden'}
        style={!isDesktopViewport && viewMode === 'sidebar_view' ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : undefined}
      >
          {viewMode === 'dashboard' ? (
              <div
                ref={dashboardScrollContainerRef}
                onScroll={handleDashboardScroll}
                className={`${isDesktopViewport ? 'h-full overflow-y-auto overflow-x-visible bg-slate-50 px-6 py-6' : isMobileArchivePanel ? 'h-full overflow-y-auto overflow-x-visible bg-[#faf9f7] p-0' : 'h-full overflow-y-auto overflow-x-visible bg-slate-50 p-4 lg:p-8'}`}
              >
                {renderDashboard()}
              </div>
          ) : renderSidebarView()}
      </div>
      {viewMode === 'dashboard' && !isDesktopViewport && (
        <div className={`mobile-tabbar lg:hidden fixed bottom-0 left-0 right-0 z-50 ${effectiveMobileDockStyle === 'default' ? 'mobile-tabbar-default' : 'pb-safe'}`}>
          {effectiveMobileDockStyle === 'ios' ? (
            <div className="mobile-tabbar-container">
              <div className="mobile-tabbar-inner">
                {renderMobileTabbarButtons()}
              </div>
            </div>
          ) : (
            <div className="mobile-tabbar-inner">
              {renderMobileTabbarButtons()}
            </div>
          )}
        </div>
      )}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`bg-white rounded-2xl shadow-2xl w-full overflow-hidden relative animate-fade-in-up ${isDesktopViewport ? 'max-w-5xl' : 'max-w-md'}`}>
            <button onClick={() => setShowAuthModal(false)} className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors z-10">
              <X className="w-5 h-5 text-slate-400" />
            </button>
            <div className={`${isDesktopViewport ? 'p-8 max-h-[86vh] overflow-y-auto' : 'p-6'}`}>
              {!isLoggedIn ? (
                <div className={isDesktopViewport ? 'mx-auto max-w-2xl' : ''}>
                  <Auth onLogin={handleLoginSuccess} />
                </div>
              ) : (
                <div className={isDesktopViewport ? 'mx-auto max-w-4xl py-2' : 'py-3'}>
                  {renderUserPanel(true)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showProModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative animate-fade-in-up p-6">
            <button onClick={() => setShowProModal(false)} className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
            <div className="pr-8">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-amber-500" />
                <h3 className="text-xl font-bold text-slate-900">{lockedToolTierLabel} 会员功能</h3>
              </div>
              <p className="text-slate-500 text-sm mb-4">
                {lockedToolName ? `「${lockedToolName}」为 ${lockedToolTierLabel} 功能。` : `该功能为 ${lockedToolTierLabel} 功能。`}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left">
                <p className="text-sm text-slate-700 font-semibold mb-2">添加轩哥微信，为您开通 {lockedToolTierLabel} 权限</p>
                <button onClick={copyWechat} className="inline-flex items-center gap-2 text-indigo-600 font-bold hover:text-indigo-700 mb-3">
                  {WECHAT_ID}
                  <Copy className="w-3 h-3" />
                </button>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={activationCode}
                    onChange={(e) => setActivationCode(e.target.value)}
                    placeholder="请输入激活码"
                    className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    onClick={handleActivateSubmit}
                    disabled={activationLoading}
                    className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-70"
                  >
                    {activationLoading ? '提交中...' : '立即激活'}
                  </button>
                </div>
                {activationResult && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${activationResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-rose-50 border border-rose-200 text-rose-700'}`}>
                    {activationResult.msg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <TeacherContactModal
        open={teacherModalOpen}
        onClose={() => setTeacherModalOpen(false)}
        title={teacherModalTitle}
        description={teacherModalDesc}
        wechatId={WECHAT_ID}
        mode={teacherModalMode}
      />
    </div>
  );
};

export default App;
